import { useState, useEffect, useMemo, lazy, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

import {
  Dumbbell, Users, Plus, LogOut, Check, Clock, ChevronRight, Copy, UserPlus,
  Play, Send, Camera, MessageCircle, Calendar, TrendingUp, AlertCircle,
  MoreHorizontal, Trash2, Video, Weight, Timer, ChevronDown, X,
  ClipboardList, BarChart3, Settings, Bell, Image, Upload, Repeat, Megaphone,
  CheckCircle2, Flame, ArrowRight, Trophy, Star, Zap,
  Edit, Save, Droplet,
  Activity, Heart, Footprints, CreditCard,
  Route as RouteIcon, Target, Flag, Search, Menu, SlidersHorizontal, Moon, Building2,
  LifeBuoy, User as UserIcon, Wallet, UserMinus, FileDown
} from "lucide-react";

import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { api, soft, BACKEND_URL, API } from "../api/client";
import { HapticService } from "../services/haptic.service";
import HelpButton from "../components/HelpButton";
import SessionCalendar from "../components/SessionCalendar";
const TeamScheduleBoard = lazy(() => import("../components/TeamScheduleBoard"));
const TrainerSettings = lazy(() => import("../components/TrainerSettings"));
const AdminConfigPanel = lazy(() => import("../components/AdminConfigPanel"));
const OrganizationPanel = lazy(() => import("../components/OrganizationPanel"));
const DietGenerator = lazy(() => import("../components/DietGenerator"));
const RecipeEditor = lazy(() => import("../components/RecipeEditor"));

import ActivityTab from "../components/trainer/ActivityTab";
import AlertsTab from "../components/trainer/AlertsTab";
import AnalyticsTab from "../components/trainer/AnalyticsTab";
import AutomationsTab from "../components/trainer/AutomationsTab";
import CalendarTab from "../components/trainer/CalendarTab";
import { ownSubTree } from "../lib/teamTree";
import ClientsTab from "../components/trainer/ClientsTab";
import ExercisesTab from "../components/trainer/ExercisesTab";
import FilesTab from "../components/trainer/FilesTab";
import FormsTab from "../components/trainer/FormsTab";
import GroupsTab from "../components/trainer/GroupsTab";
import HabitsTab from "../components/trainer/HabitsTab";
import MetricsTab from "../components/trainer/MetricsTab";
import NutritionTab from "../components/trainer/NutritionTab";
import PaymentsTab from "../components/trainer/PaymentsTab";
import FinanceTab from "../components/trainer/FinanceTab";
import RunningTab from "../components/trainer/RunningTab";
import TemplatesTab from "../components/trainer/TemplatesTab";
import VideosTab from "../components/trainer/VideosTab";
import { flatClients } from "../lib/clientList";
import GuestBanner from "../components/GuestBanner";
// ThemeSelector removed — themes available via Settings only

const TrainerDashboard = () => {
  const { user, token, logout, updateUser, onNotification, notifications: realtimeNotifs, markNotificationsRead } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [habits, setHabits] = useState([]);
  const [techniqueVideos, setTechniqueVideos] = useState([]);
  const [teamData, setTeamData] = useState({ sub_trainers: [], parent_trainer: null });
  const [plan, setPlan] = useState(null);
  const [customMetrics, setCustomMetrics] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [activeTab, setActiveTab] = useState("calendar");
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [auditLogs, setAuditLogs] = useState([]);

  // Search states
  const [searchClients, setSearchClients] = useState("");
  const [searchExercises, setSearchExercises] = useState("");
  // Katalog ExerciseDB nie niesie poziomu/biomechaniki — tylko sprzęt i partie
  // mięśniowe. Poziom/mechanika ruchu przyszły dopiero z importu Free Exercise DB.
  const [equipmentFilter, setEquipmentFilter] = useState("");
  const [muscleGroupFilter, setMuscleGroupFilter] = useState("");
  const [levelFilter, setLevelFilter] = useState("");
  const [mechanicFilter, setMechanicFilter] = useState("");
  const [searchHabits, setSearchHabits] = useState("");
  const [searchForms, setSearchForms] = useState("");
  const [searchSessions, setSearchSessions] = useState("");

  // Filtered lists (client-side)
  // Dialog states
  const [exerciseOpen, setExerciseOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const [habitOpen, setHabitOpen] = useState(false);
  const [videoReviewOpen, setVideoReviewOpen] = useState(false);
  const [clientEditOpen, setClientEditOpen] = useState(false);
  const [joinTeamOpen, setJoinTeamOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [editingExercise, setEditingExercise] = useState(null);
  const [editingHabit, setEditingHabit] = useState(null);
  const [editingClient, setEditingClient] = useState(null);
  const [newExercise, setNewExercise] = useState({ name: "", category: "strength", description: "", video_url: "", tabata_work: "", tabata_rest: "", tabata_rounds: "" });
  const [newPlan, setNewPlan] = useState({ name: "", client_id: "", exercises: [] });
  const [newHabit, setNewHabit] = useState({ name: "", description: "" });
  const [clientNotes, setClientNotes] = useState({ notes: "", goals: "", location_ids: [] });
  const [newComment, setNewComment] = useState("");
  const [joinTeamCode, setJoinTeamCode] = useState("");
  const [metricOpen, setMetricOpen] = useState(false);
  const [editingMetric, setEditingMetric] = useState(null);
  const [newMetric, setNewMetric] = useState({ name: "", unit: "", min_value: "", max_value: "", icon: "Activity" });
  const [forms, setForms] = useState([]);
  const [formResponses, setFormResponses] = useState([]);
  const [formBuilderOpen, setFormBuilderOpen] = useState(false);
  const [assignFormOpen, setAssignFormOpen] = useState(false);
  const [viewResponsesOpen, setViewResponsesOpen] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  const [newForm, setNewForm] = useState({ name: "", type: "check_in", fields: [] });
  const [assignData, setAssignData] = useState({ form_id: "", client_id: "", frequency: "once" });
  const [selectedFormResponses, setSelectedFormResponses] = useState([]);
  const [automations, setAutomations] = useState([]);
  const [automationLogs, setAutomationLogs] = useState([]);
  const [automationOpen, setAutomationOpen] = useState(false);
  const [editingAutomation, setEditingAutomation] = useState(null);
  const [newAutomation, setNewAutomation] = useState({ name: "", trigger_type: "new_client", trigger_params: {}, action_type: "send_message", action_params: { text: "" } });
  const [programTemplates, setProgramTemplates] = useState([]);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [newTemplate, setNewTemplate] = useState({ name: "", category: "general", description: "", exercises: [] });
  const [assignTemplateOpen, setAssignTemplateOpen] = useState(false);
  const [assignTemplateData, setAssignTemplateData] = useState({ template_id: "", client_id: "", scheduled_date: "" });
  const [mealPlans, setMealPlans] = useState([]);
  const [mealPlanOpen, setMealPlanOpen] = useState(false);
  const [newMealPlan, setNewMealPlan] = useState({ name: "", client_id: "", target_calories: "", target_protein: "", target_carbs: "", target_fat: "", meals: [] });
  const [sessions, setSessions] = useState([]);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignSession, setReassignSession] = useState(null);
  const [reassignTo, setReassignTo] = useState("");
  const [firmTrainers, setFirmTrainers] = useState([]);
  const [firmLocations, setFirmLocations] = useState([]);
  const [orgInfo, setOrgInfo] = useState(null); // { organization, role, is_owner } from /organizations/me
  const [sessionOpen, setSessionOpen] = useState(false);
  const [newSession, setNewSession] = useState({ client_id: "", date: "", time: "10:00", duration: "60", type: "training", notes: "" });
  // Ratings
  const [ratingSummary, setRatingSummary] = useState(null);
  // KPI
  const [kpiData, setKpiData] = useState(null);
  // Availability
  const [calendarSubTab, setCalendarSubTab] = useState("sessions"); // "sessions" | "availability"
  const [sessionsView, setSessionsView] = useState("month"); // "month" | "week" | "list"
  const [calendarScope, setCalendarScope] = useState("mine");  // "mine" | "team"
  const [gifPreview, setGifPreview] = useState(null); // { url, name }
  const [availabilitySlots, setAvailabilitySlots] = useState([]);
  const [availabilityExceptions, setAvailabilityExceptions] = useState([]);
  const [availSlotOpen, setAvailSlotOpen] = useState(false);
  const [newAvailSlot, setNewAvailSlot] = useState({ day_of_week: 0, slots: [{ start_time: "09:00", end_time: "10:00" }], location: "", location_ids: [], max_clients_per_slot: 1 });
  const [exceptionOpen, setExceptionOpen] = useState(false);
  const [newException, setNewException] = useState({ date: "", available: false, reason: "" });
  // Leaves
  const [trainerLeaves, setTrainerLeaves] = useState([]);
  const [leaveSummary, setLeaveSummary] = useState(null);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [newLeave, setNewLeave] = useState({ date_from: "", date_to: "", type: "vacation", reason: "", document_url: "" });
  const [analytics, setAnalytics] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const [clientGroups, setClientGroups] = useState([]);
  const [groupOpen, setGroupOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [newGroup, setNewGroup] = useState({ name: "", description: "", location: "", client_ids: [] });
  const [groupMessageOpen, setGroupMessageOpen] = useState(false);
  const [groupMessageTarget, setGroupMessageTarget] = useState(null);
  const [groupMessageText, setGroupMessageText] = useState("");
  const [sharedFiles, setSharedFiles] = useState([]);
  const [fileShareOpen, setFileShareOpen] = useState(false);
  const [newSharedFile, setNewSharedFile] = useState({ name: "", url: "", file_type: "pdf", client_id: "", notes: "" });
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [clientSubscriptions, setClientSubscriptions] = useState([]);
  const [payments, setPayments] = useState([]);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [subPlanOpen, setSubPlanOpen] = useState(false);
  const [newPlanData, setNewPlanData] = useState({ name: "", price: "", duration_days: "30", description: "", features: "" });
  const [assignSubOpen, setAssignSubOpen] = useState(false);
  const [assignSubData, setAssignSubData] = useState({ client_id: "", plan_id: "", location_id: "" });
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentTarget, setPaymentTarget] = useState(null);
  const [paymentData, setPaymentData] = useState({ amount: "", method: "transfer", notes: "" });
  // Cardio (Trainer)
  const [trainerRunningPlans, setTrainerRunningPlans] = useState([]);
  const [trainerRunningPlanOpen, setTrainerRunningPlanOpen] = useState(false);
  const [newTrainerRunningPlan, setNewTrainerRunningPlan] = useState({
    name: "", description: "", goal: "", difficulty: "medium",
    intervals: [], tags: [], estimated_distance_km: "", estimated_duration_minutes: "", client_id: "",
    activity_type: "running"
  });
  const [newTrainerInterval, setNewTrainerInterval] = useState({ type: "run", duration_seconds: 300, target_pace_min: "", target_pace_max: "", description: "" });
  const [assignRunningPlanOpen, setAssignRunningPlanOpen] = useState(false);
  const [assignRunningPlanId, setAssignRunningPlanId] = useState("");
  const [assignRunningClientId, setAssignRunningClientId] = useState("");

  // AI Workout Plan
  const [aiWorkoutOpen, setAiWorkoutOpen] = useState(false);
  const [aiWorkoutLoading, setAiWorkoutLoading] = useState(false);
  const [aiWorkoutForm, setAiWorkoutForm] = useState({ client_id: "", goal: "strength", days_per_week: 3, session_minutes: 60, equipment: "full_gym", notes: "" });
  const [aiWorkoutPlan, setAiWorkoutPlan] = useState(null);
  const [aiWorkoutNotes, setAiWorkoutNotes] = useState("");

  const trainerIntervalTypeNames = { warmup: "Rozgrzewka", run: "Bieg", recovery: "Odpoczynek", sprint: "Sprint", cooldown: "Schładzanie", walk: "Marsz" };
  const trainerDifficultyLabels = { easy: "Łatwy", medium: "Średni", hard: "Trudny", expert: "Ekspert" };
  const trainerFormatRunTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  const [clientTrainerFilter, setClientTrainerFilter] = useState("");
  const [clientLocationFilter, setClientLocationFilter] = useState("");

  // Filtered lists — must be after all state declarations to avoid TDZ
  const filteredClients = useMemo(() => {
    let list = clients;
    if (searchClients) {
      const q = searchClients.toLowerCase();
      list = list.filter(c => c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q));
    }
    if (clientTrainerFilter) {
      list = list.filter(c => (c.assigned_trainers || []).some(t => t.id === clientTrainerFilter));
    }
    if (clientLocationFilter) {
      list = list.filter(c => (c.location_ids || []).includes(clientLocationFilter));
    }
    return list;
  }, [clients, searchClients, clientTrainerFilter, clientLocationFilter]);

  // Opcje filtra trenera — z faktycznie przypisanych trenerów widocznych
  // klientów, nie z całego drzewka (np. trener bez żadnego klienta w
  // filtrze i tak nie zawężałby niczego).
  const clientTrainerOptions = useMemo(() => {
    const seen = new Map();
    for (const c of clients) {
      for (const t of (c.assigned_trainers || [])) seen.set(t.id, t.name);
    }
    return [...seen.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [clients]);

  const filteredExercises = useMemo(() => {
    let list = exercises;
    if (searchExercises) {
      const q = searchExercises.toLowerCase();
      list = list.filter(e => e.name?.toLowerCase().includes(q) || e.category?.toLowerCase().includes(q));
    }
    if (equipmentFilter) list = list.filter(e => e.equipment === equipmentFilter);
    if (muscleGroupFilter) list = list.filter(e => (e.muscle_groups || []).includes(muscleGroupFilter));
    if (levelFilter) list = list.filter(e => e.level === levelFilter);
    if (mechanicFilter) list = list.filter(e => e.mechanic === mechanicFilter);
    return list;
  }, [exercises, searchExercises, equipmentFilter, muscleGroupFilter, levelFilter, mechanicFilter]);

  // Opcje filtrów liczone z tego, co faktycznie jest w załadowanym katalogu —
  // stała lista wpisana na sztywno rozjechałaby się z importem z ExerciseDB.
  const exerciseEquipmentOptions = useMemo(() =>
    [...new Set(exercises.map(e => e.equipment).filter(Boolean))].sort(),
    [exercises]);
  // Puste u ćwiczeń z ExerciseDB (nie ma tam poziomu/mechaniki) — filtr po
  // prostu nie zawęzi tych rekordów, tylko pokaże opcje z Free Exercise DB.
  const exerciseLevelOptions = useMemo(() =>
    [...new Set(exercises.map(e => e.level).filter(Boolean))],
    [exercises]);
  const exerciseMechanicOptions = useMemo(() =>
    [...new Set(exercises.map(e => e.mechanic).filter(Boolean))],
    [exercises]);
  const exerciseMuscleGroupOptions = useMemo(() =>
    [...new Set(exercises.flatMap(e => e.muscle_groups || []))].sort(),
    [exercises]);

  const filteredHabits = useMemo(() => {
    if (!searchHabits) return habits;
    const q = searchHabits.toLowerCase();
    return habits.filter(h => h.name?.toLowerCase().includes(q));
  }, [habits, searchHabits]);

  const filteredForms = useMemo(() => {
    if (!searchForms) return forms;
    const q = searchForms.toLowerCase();
    return forms.filter(f => f.name?.toLowerCase().includes(q) || f.type?.toLowerCase().includes(q));
  }, [forms, searchForms]);

  const filteredSessions = useMemo(() => {
    if (!searchSessions) return sessions;
    const q = searchSessions.toLowerCase();
    return sessions.filter(s => s.client_name?.toLowerCase().includes(q));
  }, [sessions, searchSessions]);

  useEffect(() => {
    fetchData();
  }, []);

  // Tenant context — drives role-based visibility of admin tabs.
  useEffect(() => {
    if (!token) return;
    api("get", "/organizations/me", null, token)
      .then((r) => setOrgInfo(r.data))
      .catch(() => setOrgInfo(null));
  }, [token]);

  // Firm employees (organization_role trainer/manager in a business org) don't manage
  // billing, the firm panel, the audit feed, or per-org config — those are owner/admin tools.
  // Solo trainers own their personal org (role "owner") so they keep Payments/Activity/Config.
  const isBusinessOrg = orgInfo?.organization?.type === "business";
  const orgRole = orgInfo?.role || user.organization_role || "owner";
  const isFirmAdmin = isBusinessOrg && (orgRole === "owner" || orgRole === "admin");
  const showAdminTools = !isBusinessOrg || isFirmAdmin; // payments, activity, config
  const showFirmPanel = isFirmAdmin;                    // firm management panel

  // Właściciel/manager zaczynają w widoku Lista — potrzebują przeglądu
  // całości. Zwykły trener (mało klientów, jedno drzewko go nie przytłacza)
  // zostaje przy Drzewku, jak dotąd.
  const [clientsView, setClientsView] = useState(orgRole === "trainer" ? "tree" : "list");

  // Ogłoszenie do zespołu — jedyny sposób na dotarcie do wielu trenerów naraz
  // to dotąd było pisanie do każdego z osobna. Widoczne dla owner/admin/manager
  // (get_scope_trainer_ids w backendzie i tak ogranicza managera do jego
  // lokalizacji, więc dodatkowa kontrola widoczności tu jest tylko kosmetyczna).
  const canBroadcast = isBusinessOrg && ["owner", "admin", "manager"].includes(orgRole);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [broadcastText, setBroadcastText] = useState("");
  const [broadcastSending, setBroadcastSending] = useState(false);
  const sendBroadcast = async () => {
    if (!broadcastText.trim()) return;
    setBroadcastSending(true);
    try {
      const res = await api("post", "/organizations/broadcast", { text: broadcastText.trim() }, token);
      toast.success(`Wysłano do ${res.data.sent_count} ${res.data.sent_count === 1 ? "trenera" : "trenerów"}`);
      setBroadcastOpen(false);
      setBroadcastText("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nie udało się wysłać ogłoszenia");
    } finally {
      setBroadcastSending(false);
    }
  };


  // Real-time notification listener
  useEffect(() => {
    if (!onNotification) return;
    return onNotification((notif) => {
      toast.info(notif.title || "Nowe powiadomienie", { description: notif.message });
      setUnreadCount(prev => prev + 1);
      // Refresh data if session notification (new booking etc.)
      if (notif.type === "session") fetchData();
    });
  }, [onNotification]);

  // ==================== AI WORKOUT FUNCTIONS ====================

  const handleGenerateAiWorkout = async () => {
    if (!aiWorkoutForm.client_id) { toast.error("Wybierz klienta"); return; }
    setAiWorkoutLoading(true);
    try {
      const res = await api("post", "/ai/generate-workout-plan", aiWorkoutForm, token);
      setAiWorkoutPlan(res.data.plan);
      setAiWorkoutNotes(res.data.coaching_notes || "");
      toast.success("Plan AI wygenerowany");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Blad generowania planu AI");
    } finally {
      setAiWorkoutLoading(false);
    }
  };

  const handleAcceptAiWorkout = async () => {
    if (!aiWorkoutPlan) return;
    try {
      const planData = {
        name: aiWorkoutPlan.name || "Plan AI",
        client_id: aiWorkoutForm.client_id,
        exercises: (aiWorkoutPlan.exercises || []).map(ex => ({
          exercise_id: ex.exercise_id,
          order: ex.order,
          sets: (ex.sets || []).map(s => ({
            set_number: s.set_number,
            target_reps: s.target_reps,
            target_weight: s.target_weight || null,
            rest_seconds: s.rest_seconds || 60
          })),
          notes: ex.notes || ""
        })),
        notes: aiWorkoutPlan.notes || "",
        scheduled_date: new Date().toISOString().split('T')[0]
      };
      await api("post", "/workout-plans", planData, token);
      toast.success("Plan treningowy zapisany");
      setAiWorkoutOpen(false);
      setAiWorkoutPlan(null);
      setAiWorkoutNotes("");
      fetchData();
    } catch (err) {
      toast.error("Blad zapisu planu");
    }
  };

  // Osobno od wielkiego Promise.all w fetchData: stan planu jest informacją
  // poboczną i jego błąd nie może zgasić całego pulpitu — dokładnie to zrobiło
  // wcześniej podsumowanie ocen.
  const loadPlan = async () => {
    try {
      const r = await api("get", "/me/plan", null, token);
      setPlan(r.data);
    } catch {
      setPlan(null);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    loadPlan();
    try {
      const [clientsRes, alertsRes, exercisesRes, habitsRes, videosRes, teamRes, metricsRes, formsRes, responsesRes, automationsRes, automationLogsRes, templatesRes, mealPlansRes, sessionsRes, analyticsRes, unreadRes, groupsRes, filesRes, subPlansRes, subClientsRes, paymentsRes, paymentSumRes, trainerRunPlansRes, auditRes, availSlotsRes, availExceptionsRes, ratingsSummaryRes, kpiRes, leavesRes] = await Promise.all([
        soft(api("get", "/trainer/clients", null, token), [], "/trainer/clients"),
        soft(api("get", "/trainer/alerts", null, token), [], "/trainer/alerts"),
        soft(api("get", "/exercises", null, token), [], "/exercises"),
        api("get", "/habits", null, token).catch(() => ({ data: [] })),
        api("get", "/technique-videos", null, token).catch(() => ({ data: [] })),
        api("get", "/trainer/team", null, token).catch(() => ({ data: { tree: [], parent_trainer: null, me: null } })),
        api("get", "/custom-metrics", null, token).catch(() => ({ data: [] })),
        api("get", "/forms", null, token).catch(() => ({ data: [] })),
        api("get", "/form-responses", null, token).catch(() => ({ data: [] })),
        api("get", "/automations", null, token).catch(() => ({ data: [] })),
        api("get", "/automation-logs", null, token).catch(() => ({ data: [] })),
        api("get", "/program-templates", null, token).catch(() => ({ data: [] })),
        api("get", "/meal-plans", null, token).catch(() => ({ data: [] })),
        api("get", "/sessions", null, token).catch(() => ({ data: [] })),
        api("get", "/analytics", null, token).catch(() => ({ data: null })),
        api("get", "/notifications/unread-count", null, token).catch(() => ({ data: { count: 0 } })),
        api("get", "/client-groups", null, token).catch(() => ({ data: [] })),
        api("get", "/shared-files", null, token).catch(() => ({ data: [] })),
        api("get", "/subscription-plans", null, token).catch(() => ({ data: [] })),
        api("get", "/client-subscriptions", null, token).catch(() => ({ data: [] })),
        api("get", "/payments", null, token).catch(() => ({ data: [] })),
        api("get", "/payments/summary", null, token).catch(() => ({ data: null })),
        api("get", "/trainer/running-plans", null, token).catch(() => ({ data: [] })),
        api("get", "/audit-logs/recent", null, token).catch(() => ({ data: [] })),
        api("get", "/availability/slots", null, token).catch(() => ({ data: [] })),
        api("get", "/availability/exceptions", null, token).catch(() => ({ data: [] })),
        api("get", "/ratings/my", null, token).catch(() => ({ data: null })),
        api("get", "/settlements/kpi", null, token).catch(() => ({ data: null })),
        api("get", "/leaves", null, token).catch(() => ({ data: { leaves: [], summary: null } }))
      ]);
      setClients(clientsRes.data);
      setAlerts(alertsRes.data);
      setExercises(exercisesRes.data);
      setHabits(habitsRes.data || []);
      setTechniqueVideos(videosRes.data || []);
      setTeamData(teamRes.data || { tree: [], parent_trainer: null, me: null });
      setCustomMetrics(metricsRes.data || []);
      setForms(formsRes.data || []);
      setFormResponses(responsesRes.data || []);
      setAutomations(automationsRes.data || []);
      setAutomationLogs(automationLogsRes.data || []);
      setProgramTemplates(templatesRes.data || []);
      setMealPlans(mealPlansRes.data || []);
      setSessions(sessionsRes.data || []);
      setAnalytics(analyticsRes.data);
      setUnreadCount(unreadRes.data?.count || 0);
      setClientGroups(groupsRes.data || []);
      setSharedFiles(filesRes.data || []);
      setSubscriptionPlans(subPlansRes.data || []);
      setClientSubscriptions(subClientsRes.data || []);
      setPayments(paymentsRes.data || []);
      setPaymentSummary(paymentSumRes.data);
      setTrainerRunningPlans(trainerRunPlansRes.data || []);
      setAuditLogs(auditRes.data || []);
      setAvailabilitySlots(availSlotsRes.data || []);
      setAvailabilityExceptions(availExceptionsRes.data || []);
      setRatingSummary(ratingsSummaryRes.data);
      setKpiData(kpiRes.data);
      setTrainerLeaves(leavesRes.data?.leaves || []);
      setLeaveSummary(leavesRes.data?.summary || null);
    } catch (err) {
      toast.error("Nie udało się załadować danych");
    } finally {
      setLoading(false);
    }
  };

  const createExercise = async () => {
    const payload = {
      ...newExercise,
      tabata_work: newExercise.tabata_work ? parseInt(newExercise.tabata_work) : null,
      tabata_rest: newExercise.tabata_rest ? parseInt(newExercise.tabata_rest) : null,
      tabata_rounds: newExercise.tabata_rounds ? parseInt(newExercise.tabata_rounds) : null,
    };
    
    try {
      if (editingExercise) {
        await api("put", `/exercises/${editingExercise.id}`, payload, token);
        toast.success("Ćwiczenie zaktualizowane");
      } else {
        await api("post", "/exercises", payload, token);
        toast.success("Ćwiczenie utworzone");
      }
      setExerciseOpen(false);
      setEditingExercise(null);
      setNewExercise({ name: "", category: "strength", description: "", video_url: "", tabata_work: "", tabata_rest: "", tabata_rounds: "" });
      fetchData();
    } catch (err) {
      toast.error("Nie udało się zapisać");
    }
  };

  const deleteExercise = async (exerciseId) => {
    if (!window.confirm("Czy na pewno chcesz usunąć to ćwiczenie?")) return;
    try {
      await api("delete", `/exercises/${exerciseId}`, null, token);
      toast.success("Ćwiczenie usunięte");
      fetchData();
    } catch (err) {
      toast.error("Nie udało się usunąć");
    }
  };

  const createHabit = async () => {
    try {
      await api("post", "/habits", newHabit, token);
      toast.success("Habit created");
      setHabitOpen(false);
      setNewHabit({ name: "", description: "" });
      fetchData();
    } catch (err) {
      toast.error("Failed to create");
    }
  };

  const createOrUpdateHabit = async () => {
    try {
      if (editingHabit) {
        await api("put", `/habits/${editingHabit.id}`, newHabit, token);
        toast.success("Nawyk zaktualizowany");
      } else {
        await api("post", "/habits", newHabit, token);
        toast.success("Nawyk utworzony");
      }
      setHabitOpen(false);
      setEditingHabit(null);
      setNewHabit({ name: "", description: "" });
      fetchData();
    } catch (err) {
      toast.error("Nie udało się zapisać");
    }
  };

  const deleteHabit = async (habitId) => {
    if (!window.confirm("Czy na pewno chcesz usunąć ten nawyk?")) return;
    try {
      await api("delete", `/habits/${habitId}`, null, token);
      toast.success("Nawyk usunięty");
      fetchData();
    } catch (err) {
      toast.error("Nie udało się usunąć");
    }
  };

  const createMetric = async () => {
    const payload = {
      name: newMetric.name,
      unit: newMetric.unit,
      min_value: newMetric.min_value !== "" ? parseFloat(newMetric.min_value) : null,
      max_value: newMetric.max_value !== "" ? parseFloat(newMetric.max_value) : null,
      icon: newMetric.icon
    };
    try {
      if (editingMetric) {
        await api("put", `/custom-metrics/${editingMetric.id}`, payload, token);
        toast.success("Metryka zaktualizowana");
      } else {
        await api("post", "/custom-metrics", payload, token);
        toast.success("Metryka utworzona");
      }
      setMetricOpen(false);
      setEditingMetric(null);
      setNewMetric({ name: "", unit: "", min_value: "", max_value: "", icon: "Activity" });
      fetchData();
    } catch (err) {
      toast.error("Nie udało się zapisać");
    }
  };

  const deleteMetric = async (metricId) => {
    if (!window.confirm("Czy na pewno chcesz usunąć tę metrykę?")) return;
    try {
      await api("delete", `/custom-metrics/${metricId}`, null, token);
      toast.success("Metryka usunięta");
      fetchData();
    } catch (err) {
      toast.error("Nie udało się usunąć");
    }
  };

  const openMetricEdit = (metric) => {
    setEditingMetric(metric);
    setNewMetric({
      name: metric.name,
      unit: metric.unit,
      min_value: metric.min_value ?? "",
      max_value: metric.max_value ?? "",
      icon: metric.icon || "Activity"
    });
    setMetricOpen(true);
  };

  const metricIconMap = { Activity, Heart, Moon, Footprints, Flame, Star, Zap, TrendingUp, Droplet };

  const createForm = async () => {
    if (!newForm.name || newForm.fields.length === 0) {
      toast.error("Podaj nazwę i dodaj minimum 1 pole");
      return;
    }
    try {
      if (editingForm) {
        await api("put", `/forms/${editingForm.id}`, newForm, token);
        toast.success("Formularz zaktualizowany");
      } else {
        await api("post", "/forms", newForm, token);
        toast.success("Formularz utworzony");
      }
      setFormBuilderOpen(false);
      setEditingForm(null);
      setNewForm({ name: "", type: "check_in", fields: [] });
      fetchData();
    } catch (err) {
      toast.error("Nie udało się zapisać");
    }
  };

  const deleteForm = async (formId) => {
    if (!window.confirm("Czy na pewno chcesz usunąć ten formularz?")) return;
    try {
      await api("delete", `/forms/${formId}`, null, token);
      toast.success("Formularz usunięty");
      fetchData();
    } catch (err) {
      toast.error("Nie udało się usunąć");
    }
  };

  const assignForm = async () => {
    try {
      await api("post", "/form-assignments", assignData, token);
      toast.success("Formularz przypisany");
      setAssignFormOpen(false);
      setAssignData({ form_id: "", client_id: "", frequency: "once" });
    } catch (err) {
      toast.error("Nie udało się przypisać");
    }
  };

  const addFormField = () => {
    setNewForm(prev => ({
      ...prev,
      fields: [...prev.fields, { name: `field_${prev.fields.length + 1}`, label: "", type: "text", required: true, options: [] }]
    }));
  };

  const updateFormField = (index, updates) => {
    setNewForm(prev => ({
      ...prev,
      fields: prev.fields.map((f, i) => i === index ? { ...f, ...updates } : f)
    }));
  };

  const removeFormField = (index) => {
    setNewForm(prev => ({
      ...prev,
      fields: prev.fields.filter((_, i) => i !== index)
    }));
  };

  const viewFormResponses = (formId) => {
    const filtered = formResponses.filter(r => r.form_id === formId);
    setSelectedFormResponses(filtered);
    setViewResponsesOpen(true);
  };

  const createAutomation = async () => {
    try {
      const payload = { ...newAutomation };
      if (editingAutomation) {
        await api("put", `/automations/${editingAutomation.id}`, payload, token);
        toast.success("Automatyzacja zaktualizowana");
      } else {
        await api("post", "/automations", payload, token);
        toast.success("Automatyzacja utworzona");
      }
      setAutomationOpen(false);
      setEditingAutomation(null);
      setNewAutomation({ name: "", trigger_type: "new_client", trigger_params: {}, action_type: "send_message", action_params: { text: "" } });
      fetchData();
    } catch (err) {
      toast.error("Nie udało się zapisać");
    }
  };

  const deleteAutomation = async (automationId) => {
    if (!window.confirm("Czy na pewno chcesz usunąć tę automatyzację?")) return;
    try {
      await api("delete", `/automations/${automationId}`, null, token);
      toast.success("Automatyzacja usunięta");
      fetchData();
    } catch (err) {
      toast.error("Nie udało się usunąć");
    }
  };

  const toggleAutomation = async (automation) => {
    try {
      await api("put", `/automations/${automation.id}`, { ...automation, enabled: !automation.enabled }, token);
      toast.success(automation.enabled ? "Wyłączono" : "Włączono");
      fetchData();
    } catch (err) {
      toast.error("Nie udało się zmienić statusu");
    }
  };

  const triggerLabels = {
    new_client: "Nowy klient",
    no_workout_x_days: "Brak treningu X dni",
    check_in_submitted: "Check-in wysłany",
    plan_completed: "Plan ukończony",
    measurement_logged: "Pomiar zapisany"
  };

  const actionLabels = {
    send_message: "Wyślij wiadomość",
    assign_form: "Przypisz formularz"
  };

  const createTemplate = async () => {
    try {
      const payload = {
        ...newTemplate,
        exercises: newTemplate.exercises.map((ex, i) => ({
          exercise_id: ex.id,
          order: i + 1,
          sets: Array.from({ length: ex.sets || 3 }, (_, j) => ({
            set_number: j + 1,
            target_reps: ex.reps || 10,
            target_weight: null,
            rest_seconds: 90
          }))
        }))
      };
      if (editingTemplate) {
        await api("put", `/program-templates/${editingTemplate.id}`, payload, token);
        toast.success("Szablon zaktualizowany");
      } else {
        await api("post", "/program-templates", payload, token);
        toast.success("Szablon utworzony");
      }
      setTemplateOpen(false);
      setEditingTemplate(null);
      setNewTemplate({ name: "", category: "general", description: "", exercises: [] });
      fetchData();
    } catch (err) {
      toast.error("Nie udało się zapisać");
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!window.confirm("Czy na pewno chcesz usunąć ten szablon?")) return;
    try {
      await api("delete", `/program-templates/${templateId}`, null, token);
      toast.success("Szablon usunięty");
      fetchData();
    } catch (err) {
      toast.error("Nie udało się usunąć");
    }
  };

  const assignTemplate = async () => {
    try {
      await api("post", `/program-templates/${assignTemplateData.template_id}/assign?client_id=${assignTemplateData.client_id}${assignTemplateData.scheduled_date ? '&scheduled_date=' + assignTemplateData.scheduled_date : ''}`, null, token);
      toast.success("Plan utworzony z szablonu");
      setAssignTemplateOpen(false);
      setAssignTemplateData({ template_id: "", client_id: "", scheduled_date: "" });
      fetchData();
    } catch (err) {
      toast.error("Nie udało się przypisać");
    }
  };

  const templateCategoryLabels = {
    general: "Ogólny",
    strength: "Siła",
    cardio: "Cardio",
    flexibility: "Rozciąganie",
    sport: "Sport"
  };

  const createMealPlan = async () => {
    try {
      const payload = {
        ...newMealPlan,
        target_calories: newMealPlan.target_calories ? parseFloat(newMealPlan.target_calories) : null,
        target_protein: newMealPlan.target_protein ? parseFloat(newMealPlan.target_protein) : null,
        target_carbs: newMealPlan.target_carbs ? parseFloat(newMealPlan.target_carbs) : null,
        target_fat: newMealPlan.target_fat ? parseFloat(newMealPlan.target_fat) : null,
      };
      await api("post", "/meal-plans", payload, token);
      toast.success("Plan żywieniowy utworzony");
      setMealPlanOpen(false);
      setNewMealPlan({ name: "", client_id: "", target_calories: "", target_protein: "", target_carbs: "", target_fat: "", meals: [] });
      fetchData();
    } catch (err) {
      toast.error("Nie udało się zapisać");
    }
  };

  const deleteMealPlan = async (planId) => {
    if (!window.confirm("Usunąć plan żywieniowy?")) return;
    try {
      await api("delete", `/meal-plans/${planId}`, null, token);
      toast.success("Plan usunięty");
      fetchData();
    } catch (err) {
      toast.error("Nie udało się usunąć");
    }
  };

  const addMealToPlan = (mealType) => {
    setNewMealPlan({...newMealPlan, meals: [...newMealPlan.meals, { meal_type: mealType, items: [{ name: "", calories: 0, protein: 0, carbs: 0, fat: 0, portion: "" }], notes: null }]});
  };

  const mealTypeLabels = { breakfast: "Śniadanie", lunch: "Obiad", dinner: "Kolacja", snack: "Przekąska" };

  const createSession = async () => {
    try {
      await api("post", "/sessions", {
        ...newSession,
        duration: parseInt(newSession.duration) || 60
      }, token);
      toast.success("Sesja zaplanowana");
      setSessionOpen(false);
      setNewSession({ client_id: "", date: "", time: "10:00", duration: "60", type: "training", notes: "" });
      fetchData();
    } catch (err) {
      toast.error("Nie udało się zaplanować");
    }
  };

  // Zmiana statusu sesji idzie przez /booking/*, nie przez PUT /sessions.
  // PUT tylko przestawia pole w bazie: klient nie dostawał ani powiadomienia,
  // ani maila, powód odrzucenia nie miał gdzie trafić, a prośba o ocenę
  // treningu — jedyny wyzwalacz modułu ocen — nigdy się nie odpalała (R32).
  const SESSION_ACTION_DONE = {
    confirm: "Sesja potwierdzona — klient dostał powiadomienie i mail",
    reject: "Prośba odrzucona — klient dostał powiadomienie i mail",
    complete: "Sesja zakończona — klient może ją teraz ocenić",
    cancel: "Sesja odwołana — klient dostał powiadomienie",
    absent: "Oznaczono nieobecność",
  };

  const sessionAction = async (sessionId, action, body = null) => {
    try {
      await api("post", `/booking/${sessionId}/${action}`, body, token);
      if (action === "confirm") HapticService.bookingConfirmed();
      else if (action === "reject") HapticService.bookingRejected();
      toast.success(SESSION_ACTION_DONE[action] || "Zaktualizowano");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nie udało się zaktualizować");
    }
  };

  // Odrzucenie i odwołanie niosą powód — klient widzi go w powiadomieniu
  // (przy odrzuceniu także w mailu), więc pytamy o niego zamiast wysyłać pustkę.
  const rejectSession = (sessionId) => {
    const reason = window.prompt("Powód odrzucenia — klient zobaczy go w mailu i powiadomieniu:", "");
    if (reason === null) return;
    return sessionAction(sessionId, "reject", { reason: reason.trim() });
  };

  const cancelSession = (sessionId) => {
    const reason = window.prompt("Powód odwołania — klient zobaczy go w powiadomieniu:", "");
    if (reason === null) return;
    return sessionAction(sessionId, "cancel", { reason: reason.trim() });
  };

  const markAbsent = (sessionId) => {
    if (!window.confirm("Oznaczyć klienta jako nieobecnego na tej sesji?")) return;
    return sessionAction(sessionId, "absent");
  };

  const openReassign = async (session) => {
    setReassignSession(session);
    setReassignTo("");
    if (firmTrainers.length === 0) {
      try {
        const r = await api("get", "/organizations/members", null, token);
        setFirmTrainers(r.data.members || []);
        setFirmLocations(r.data.locations || []);
      } catch { /* not in a firm */ }
    }
    setReassignOpen(true);
  };

  const doReassign = async () => {
    if (!reassignSession || !reassignTo) return;
    try {
      const r = await api("post", `/sessions/${reassignSession.id}/reassign`, { new_trainer_id: reassignTo }, token);
      toast.success(`Zastępstwo: ${r.data.trainer_name}`);
      setReassignOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nie udało się przypisać zastępstwa");
    }
  };

  // Wycofanie pakietu: backend oznacza go jako nieaktywny, więc znika z oferty,
  // a subskrypcje już przypisane zostają nietknięte.
  // Kod podaje podopieczny — on decyduje, komu go pokaże. Backend odmawia
  // przejęcia kogoś, kto ma już trenera: taką zmianę wykonuje sam zainteresowany.
  const connectClientByCode = async (code, onDone) => {
    try {
      const r = await api("post", `/trainer/connect/${code.trim()}`, null, token);
      toast.success(r.data?.pending
        ? `Wysłano prośbę do: ${r.data?.client_name || "podopieczny"} — czeka na jego zgodę`
        : `Podpięto: ${r.data?.client_name || "podopieczny"}`);
      if (onDone) onDone();
      fetchData();
      loadPlan();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nie udało się podpiąć");
    }
  };

  // Podopieczny, który nie ma (jeszcze) konta. Profil jest zwykłym klientem,
  // tylko bez hasła — plany, pomiary i sesje trafiają tam, gdzie zawsze.
  // Usunięcie trenera z zespołu. Endpoint istniał od dawna i nie miał żadnego
  // przycisku — zespół dało się tylko budować, nigdy rozmontować.
  const removeFromTeam = async (node) => {
    if (!window.confirm(`Usunąć ${node.name} z zespołu? Podopieczni zostają przy nim.`)) return;
    try {
      await api("post", `/trainer/remove-from-team/${node.id}`, null, token);
      toast.success(`${node.name} nie jest już w zespole`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nie udało się usunąć z zespołu");
    }
  };

  // Kod, którym podopieczny bierze na własność profil założony przez trenera.
  const inviteManagedClient = async (client) => {
    try {
      const r = await api("post", `/trainer/clients/${client.id}/invite`, null, token);
      window.prompt(
        `Kod dla: ${client.name}. Osoba wpisuje go w aplikacji („Mam kod od trenera") i zakłada własne konto.`,
        r.data.claim_code
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nie udało się wygenerować kodu");
    }
  };

  const createManagedClient = async (name, onDone) => {
    try {
      const r = await api("post", "/trainer/clients", { name: name.trim() }, token);
      toast.success(`Dodano: ${r.data?.name || name}`);
      if (onDone) onDone();
      fetchData();
      loadPlan();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nie udało się dodać podopiecznego");
    }
  };

  const deleteSubscriptionPlan = async (plan) => {
    if (!window.confirm(`Wycofać pakiet „${plan.name}"? Zniknie z oferty; obecne subskrypcje zostają.`)) return;
    try {
      await api("delete", `/subscription-plans/${plan.id}`, null, token);
      toast.success("Pakiet wycofany");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nie udało się wycofać pakietu");
    }
  };

  const deleteSession = async (sessionId) => {
    if (!window.confirm("Usunąć sesję?")) return;
    try {
      await api("delete", `/sessions/${sessionId}`, null, token);
      toast.success("Sesja usunięta");
      fetchData();
    } catch (err) {
      toast.error("Nie udało się usunąć");
    }
  };

  const sessionTypeLabels = { training: "Trening", consultation: "Konsultacja", assessment: "Ocena" };

  // Open the "new session" dialog, optionally pre-filled with a date (calendar day click).
  const openNewSession = async (dateStr = "") => {
    setNewSession({ client_id: "", date: dateStr, time: "10:00", duration: "60", type: "training", notes: "", location_id: "" });
    if (firmLocations.length === 0) {
      try { const r = await api("get", "/organizations/members", null, token); setFirmLocations(r.data.locations || []); } catch { /* not a firm */ }
    }
    setSessionOpen(true);
  };

  // Calendar event action → existing handlers.
  const handleCalendarAction = (action, session) => {
    if (action === "reassign") return openReassign(session);
    if (action === "delete") return deleteSession(session.id);
    if (action === "reject") return rejectSession(session.id);
    if (action === "cancel") return cancelSession(session.id);
    if (action === "absent") return markAbsent(session.id);
    if (action === "confirm" || action === "complete") return sessionAction(session.id, action);
  };

  const dayLabels = ["Poniedziałek", "Wtorek", "Środa", "Czwartek", "Piątek", "Sobota", "Niedziela"];
  const dayLabelsShort = ["Pon", "Wt", "Śr", "Czw", "Pt", "Sob", "Ndz"];

  const createAvailabilitySlot = async () => {
    try {
      await api("post", "/availability/slots", {
        ...newAvailSlot,
        max_clients_per_slot: parseInt(newAvailSlot.max_clients_per_slot) || 1,
        location: newAvailSlot.location || null,
        location_ids: newAvailSlot.location_ids || []
      }, token);
      toast.success("Dostępność dodana");
      setAvailSlotOpen(false);
      setNewAvailSlot({ day_of_week: 0, slots: [{ start_time: "09:00", end_time: "10:00" }], location: "", location_ids: [], max_clients_per_slot: 1 });
      fetchData();
    } catch (err) {
      toast.error("Nie udało się dodać dostępności");
    }
  };

  const deleteAvailabilitySlot = async (slotId) => {
    if (!window.confirm("Usunąć dostępność?")) return;
    try {
      await api("delete", `/availability/slots/${slotId}`, null, token);
      toast.success("Dostępność usunięta");
      fetchData();
    } catch (err) {
      toast.error("Nie udało się usunąć");
    }
  };

  const createException = async () => {
    try {
      await api("post", "/availability/exceptions", {
        ...newException,
        available: newException.available || false
      }, token);
      toast.success("Wyjątek dodany");
      setExceptionOpen(false);
      setNewException({ date: "", available: false, reason: "" });
      fetchData();
    } catch (err) {
      toast.error("Nie udało się dodać wyjątku");
    }
  };

  const exportFinancialCSV = async () => {
    try {
      const now = new Date();
      const date = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      const res = await api("get", `/settlements/export?period=monthly&date=${date}`, null, token);
      // Create download
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `duofit_finanse_${date}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV pobrano");
    } catch (err) {
      toast.error("Nie udało się pobrać CSV");
    }
  };

  const leaveTypeLabels = {
    vacation: t("trainer.leave.vacation"),
    sick_leave: t("trainer.leave.sickLeave"),
    training: t("trainer.leave.training"),
    personal: t("trainer.leave.personal"),
    other: t("trainer.leave.other"),
  };

  const createLeave = async () => {
    if (!newLeave.date_from || !newLeave.date_to) return;
    try {
      await api("post", "/leaves", {
        ...newLeave,
        document_url: newLeave.document_url || null,
        reason: newLeave.reason || null
      }, token);
      toast.success("Urlop zgłoszony");
      setLeaveOpen(false);
      setNewLeave({ date_from: "", date_to: "", type: "vacation", reason: "", document_url: "" });
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Nie udało się zgłosić urlopu");
    }
  };

  const deleteLeave = async (leaveId) => {
    if (!window.confirm(t("trainer.leave.cancelConfirm"))) return;
    try {
      await api("delete", `/leaves/${leaveId}`, null, token);
      toast.success("Urlop anulowany");
      fetchData();
    } catch (err) {
      toast.error("Nie udało się anulować");
    }
  };

  const deleteException = async (exceptionId) => {
    try {
      await api("delete", `/availability/exceptions/${exceptionId}`, null, token);
      toast.success("Wyjątek usunięty");
      fetchData();
    } catch (err) {
      toast.error("Nie udało się usunąć");
    }
  };

  const openNotifications = async () => {
    try {
      const res = await api("get", "/notifications", null, token);
      const dbNotifs = res.data || [];
      const allIds = new Set(dbNotifs.map(n => n.id));
      const merged = [...dbNotifs];
      for (const rn of realtimeNotifs) {
        if (!allIds.has(rn.id)) merged.unshift(rn);
      }
      setNotifications(merged);
      setNotifOpen(true);
      await api("put", "/notifications/mark-read", null, token);
      setUnreadCount(0);
      if (markNotificationsRead) markNotificationsRead();
    } catch (err) {}
  };

  const createGroup = async () => {
    try {
      if (!newGroup.name.trim()) return;
      if (editingGroup) {
        await api("put", `/client-groups/${editingGroup.id}`, newGroup, token);
        toast.success("Grupa zaktualizowana");
      } else {
        await api("post", "/client-groups", newGroup, token);
        toast.success("Grupa utworzona");
      }
      setGroupOpen(false);
      setEditingGroup(null);
      setNewGroup({ name: "", description: "", client_ids: [] });
      fetchData();
    } catch (err) {
      toast.error("Nie udało się zapisać grupy");
    }
  };

  const deleteGroup = async (groupId) => {
    try {
      await api("delete", `/client-groups/${groupId}`, null, token);
      toast.success("Grupa usunięta");
      fetchData();
    } catch (err) {
      toast.error("Nie udało się usunąć");
    }
  };

  const sendGroupMessage = async () => {
    try {
      if (!groupMessageText.trim()) return;
      await api("post", `/client-groups/${groupMessageTarget.id}/send-message`, { text: groupMessageText }, token);
      toast.success(`Wiadomość wysłana do ${groupMessageTarget.client_ids.length} klientów`);
      setGroupMessageOpen(false);
      setGroupMessageText("");
    } catch (err) {
      toast.error("Nie udało się wysłać");
    }
  };

  const createSharedFile = async () => {
    try {
      if (!newSharedFile.name.trim() || !newSharedFile.url.trim()) return;
      const payload = { ...newSharedFile, client_id: newSharedFile.client_id || null };
      await api("post", "/shared-files", payload, token);
      toast.success("Plik udostępniony");
      setFileShareOpen(false);
      setNewSharedFile({ name: "", url: "", file_type: "pdf", client_id: "", notes: "" });
      fetchData();
    } catch (err) {
      toast.error("Nie udało się udostępnić pliku");
    }
  };

  const deleteSharedFile = async (fileId) => {
    try {
      await api("delete", `/shared-files/${fileId}`, null, token);
      toast.success("Plik usunięty");
      fetchData();
    } catch (err) {
      toast.error("Nie udało się usunąć");
    }
  };

  const fileTypeIcons = { pdf: ClipboardList, image: Image, document: ClipboardList, video: Video, other: ClipboardList };

  const createSubPlan = async () => {
    try {
      if (!newPlanData.name.trim() || !newPlanData.price) return;
      await api("post", "/subscription-plans", {
        name: newPlanData.name,
        price: parseFloat(newPlanData.price),
        duration_days: parseInt(newPlanData.duration_days),
        description: newPlanData.description,
        features: newPlanData.features.split("\n").filter(f => f.trim())
      }, token);
      toast.success("Pakiet utworzony");
      setSubPlanOpen(false);
      setNewPlanData({ name: "", price: "", duration_days: "30", description: "", features: "" });
      fetchData();
    } catch (err) {
      toast.error("Nie udało się utworzyć pakietu");
    }
  };

  const assignSubscription = async () => {
    try {
      if (!assignSubData.client_id || !assignSubData.plan_id) return;
      // Puste "" -> nie wysyłamy w ogóle (backend traktuje brak jako
      // "Nieprzypisane", nie zgaduje lokalizacji za nas).
      const { location_id, ...rest } = assignSubData;
      await api("post", "/client-subscriptions", location_id ? assignSubData : rest, token);
      toast.success("Subskrypcja przypisana");
      setAssignSubOpen(false);
      setAssignSubData({ client_id: "", plan_id: "", location_id: "" });
      fetchData();
    } catch (err) {
      toast.error("Nie udało się przypisać");
    }
  };

  const recordPayment = async () => {
    try {
      if (!paymentData.amount) return;
      await api("post", "/payments", {
        subscription_id: paymentTarget.id,
        amount: parseFloat(paymentData.amount),
        method: paymentData.method,
        notes: paymentData.notes
      }, token);
      toast.success("Płatność zapisana");
      setPaymentDialogOpen(false);
      setPaymentTarget(null);
      setPaymentData({ amount: "", method: "transfer", notes: "" });
      fetchData();
    } catch (err) {
      toast.error("Nie udało się zapisać płatności");
    }
  };

  const paymentMethodLabels = { manual: "Ręcznie", transfer: "Przelew", cash: "Gotówka", card: "Karta" };

  const openClientEdit = async (client) => {
    setEditingClient(client);
    try {
      const res = await api("get", `/trainer/clients/${client.id}`, null, token);
      setClientNotes({
        notes: res.data.trainer_notes || "",
        goals: res.data.goals || "",
        location_ids: res.data.location_ids || [],
      });
    } catch (err) {
      setClientNotes({ notes: "", goals: "", location_ids: [] });
    }
    setClientEditOpen(true);
  };

  // Endpoint istniał od dawna (backend/routers/financial.py), ale nic w
  // interfejsie go nie wywoływało — trener nie miał jak wygenerować raportu.
  const downloadClientProgressPdf = async (client) => {
    try {
      const res = await fetch(`${API}/reports/client/${client.id}/progress`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        toast.error(detail.detail || "Nie udało się wygenerować raportu");
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `postep_${client.name.replace(/\s+/g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Nie udało się wygenerować raportu");
    }
  };

  const saveClientNotes = async () => {
    try {
      await api("put", `/trainer/clients/${editingClient.id}`, clientNotes, token);
      toast.success("Zapisano");
      setClientEditOpen(false);
    } catch (err) {
      toast.error("Nie udało się zapisać");
    }
  };

  const removeClient = async (clientId) => {
    if (!window.confirm("Czy na pewno chcesz usunąć tego klienta z listy?")) return;
    try {
      await api("delete", `/trainer/clients/${clientId}`, null, token);
      toast.success("Klient usunięty");
      fetchData();
    } catch (err) {
      toast.error("Nie udało się usunąć");
    }
  };

  const addVideoComment = async () => {
    if (!selectedVideo || !newComment.trim()) return;
    try {
      await api("post", `/technique-videos/${selectedVideo.id}/comment`, {
        comment: newComment
      }, token);
      toast.success("Komentarz dodany");
      setNewComment("");
      setVideoReviewOpen(false);
      fetchData();
    } catch (err) {
      toast.error("Nie udało się dodać komentarza");
    }
  };

  const createPlan = async () => {
    try {
      const planData = {
        ...newPlan,
        exercises: newPlan.exercises.map((ex, i) => ({
          exercise_id: ex.id,
          order: i + 1,
          sets: Array.from({ length: ex.sets || 3 }, (_, j) => ({
            set_number: j + 1,
            target_reps: ex.reps || 10,
            target_weight: null,
            rest_seconds: 90
          })),
          notes: ex.notes || null
        }))
      };
      await api("post", "/workout-plans", planData, token);
      toast.success("Plan created");
      setPlanOpen(false);
      setNewPlan({ name: "", client_id: "", exercises: [] });
    } catch (err) {
      toast.error("Failed to create plan");
    }
  };

  const addExerciseToPlan = (exercise) => {
    setNewPlan(prev => ({
      ...prev,
      exercises: [...prev.exercises, { ...exercise, sets: 3, reps: 10 }]
    }));
  };

  const removeExerciseFromPlan = (index) => {
    setNewPlan(prev => ({
      ...prev,
      exercises: prev.exercises.filter((_, i) => i !== index)
    }));
  };

  const copyCode = () => {
    navigator.clipboard.writeText(user.trainer_code);
    toast.success("Code copied");
  };

  // ==================== TRAINER RUNNING FUNCTIONS ====================
  const addTrainerInterval = () => {
    const interval = {
      order: newTrainerRunningPlan.intervals.length,
      type: newTrainerInterval.type,
      duration_seconds: parseInt(newTrainerInterval.duration_seconds) || 300,
      target_pace_min: newTrainerInterval.target_pace_min ? parseFloat(newTrainerInterval.target_pace_min) : null,
      target_pace_max: newTrainerInterval.target_pace_max ? parseFloat(newTrainerInterval.target_pace_max) : null,
      description: newTrainerInterval.description || ""
    };
    setNewTrainerRunningPlan(prev => ({ ...prev, intervals: [...prev.intervals, interval] }));
    setNewTrainerInterval({ type: "run", duration_seconds: 300, target_pace_min: "", target_pace_max: "", description: "" });
  };

  const removeTrainerInterval = (index) => {
    setNewTrainerRunningPlan(prev => ({
      ...prev,
      intervals: prev.intervals.filter((_, i) => i !== index).map((int, i) => ({ ...int, order: i }))
    }));
  };

  const saveTrainerRunningPlan = async () => {
    if (!newTrainerRunningPlan.name.trim()) { toast.error("Podaj nazwę planu"); return; }
    if (newTrainerRunningPlan.intervals.length === 0) { toast.error("Dodaj przynajmniej jeden interwał"); return; }
    const payload = {
      ...newTrainerRunningPlan,
      estimated_distance_km: newTrainerRunningPlan.estimated_distance_km ? parseFloat(newTrainerRunningPlan.estimated_distance_km) : null,
      estimated_duration_minutes: newTrainerRunningPlan.estimated_duration_minutes ? parseInt(newTrainerRunningPlan.estimated_duration_minutes) : null,
    };
    try {
      await api("post", "/trainer/running-plans", payload, token);
      toast.success("Plan biegowy utworzony");
      setTrainerRunningPlanOpen(false);
      setNewTrainerRunningPlan({ name: "", description: "", goal: "", difficulty: "medium", intervals: [], tags: [], estimated_distance_km: "", estimated_duration_minutes: "", client_id: "", activity_type: "running" });
      fetchData();
    } catch (err) {
      toast.error("Nie udało się utworzyć planu");
    }
  };

  const assignRunningPlan = async () => {
    if (!assignRunningPlanId || !assignRunningClientId) { toast.error("Wybierz plan i klienta"); return; }
    try {
      await api("post", `/trainer/running-plans/${assignRunningPlanId}/assign`, { client_id: assignRunningClientId }, token);
      toast.success("Plan przypisany do klienta");
      setAssignRunningPlanOpen(false);
      setAssignRunningPlanId("");
      setAssignRunningClientId("");
    } catch (err) {
      toast.error("Nie udało się przypisać planu");
    }
  };

  const deleteTrainerRunningPlan = async (planId) => {
    try {
      await api("delete", `/running-plans/${planId}`, null, token);
      toast.success("Plan usunięty");
      fetchData();
    } catch (err) {
      toast.error("Nie udało się usunąć planu");
    }
  };

  if (loading) return <div className="loading"><Dumbbell className="spin" size={32} /></div>;

  return (
    <div className="trainer-container" data-testid="trainer-dashboard">
      {/* Mobile hamburger */}
      <button className="hamburger-btn" onClick={() => setSidebarOpen(true)}>
        <Menu size={24} />
      </button>
      {/* Sidebar overlay for mobile */}
      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-brand">
          <Dumbbell size={24} strokeWidth={1.5} />
          <span>DuoFit</span>
        </div>
        
        <nav className="sidebar-nav">
          {/* Klienci group */}
          <div className="sidebar-group">
            <div className={`sidebar-group-header ${collapsedGroups.klienci ? 'collapsed' : ''}`} onClick={() => setCollapsedGroups(p => ({...p, klienci: !p.klienci}))}>
              <span>{t("nav.clients")}</span>
              <ChevronDown size={14} />
            </div>
            <div className={`sidebar-group-items ${collapsedGroups.klienci ? 'collapsed' : ''}`}>
              <button className={`nav-btn ${activeTab === 'clients' ? 'active' : ''}`} onClick={() => { setActiveTab('clients'); setSidebarOpen(false); }}>
                <Users size={18} /> {t("nav.clients")}
                {clients.length > 0 && <Badge variant="secondary">{clients.length}</Badge>}
              </button>
              <button className={`nav-btn ${activeTab === 'groups' ? 'active' : ''}`} onClick={() => { setActiveTab('groups'); setSidebarOpen(false); }}>
                <UserPlus size={18} /> {t("nav.groups")}
                {clientGroups.length > 0 && <Badge variant="secondary">{clientGroups.length}</Badge>}
              </button>
              <button className={`nav-btn ${activeTab === 'team' ? 'active' : ''}`} onClick={() => { setActiveTab('team'); setSidebarOpen(false); }}>
                <Users size={18} /> {t("nav.team")}
                {(teamData.tree || []).length > 0 && <Badge variant="secondary">{(teamData.tree || []).length}</Badge>}
              </button>
            </div>
          </div>

          {/* Trening group */}
          <div className="sidebar-group">
            <div className={`sidebar-group-header ${collapsedGroups.trening ? 'collapsed' : ''}`} onClick={() => setCollapsedGroups(p => ({...p, trening: !p.trening}))}>
              <span>{t("nav.workout")}</span>
              <ChevronDown size={14} />
            </div>
            <div className={`sidebar-group-items ${collapsedGroups.trening ? 'collapsed' : ''}`}>
              <button className={`nav-btn ${activeTab === 'exercises' ? 'active' : ''}`} onClick={() => { setActiveTab('exercises'); setSidebarOpen(false); }}>
                <Dumbbell size={18} /> {t("nav.exercises")}
              </button>
              <button className={`nav-btn ${activeTab === 'templates' ? 'active' : ''}`} onClick={() => { setActiveTab('templates'); setSidebarOpen(false); }}>
                <Repeat size={18} /> {t("nav.templates")}
                {programTemplates.length > 0 && <Badge variant="secondary">{programTemplates.length}</Badge>}
              </button>
              <button className={`nav-btn ${activeTab === 'running' ? 'active' : ''}`} onClick={() => { setActiveTab('running'); setSidebarOpen(false); }}>
                <Activity size={18} /> {t("nav.cardio")}
              </button>
              <button className={`nav-btn ${activeTab === 'calendar' ? 'active' : ''}`} onClick={() => { setActiveTab('calendar'); setSidebarOpen(false); }}>
                <Calendar size={18} /> {t("nav.calendar")}
                {sessions.filter(s => s.status === 'scheduled').length > 0 && (
                  <Badge variant="secondary">{sessions.filter(s => s.status === 'scheduled').length}</Badge>
                )}
              </button>
            </div>
          </div>

          {/* Zdrowie group */}
          <div className="sidebar-group">
            <div className={`sidebar-group-header ${collapsedGroups.zdrowie ? 'collapsed' : ''}`} onClick={() => setCollapsedGroups(p => ({...p, zdrowie: !p.zdrowie}))}>
              <span>{t("nav.group.health")}</span>
              <ChevronDown size={14} />
            </div>
            <div className={`sidebar-group-items ${collapsedGroups.zdrowie ? 'collapsed' : ''}`}>
              <button className={`nav-btn ${activeTab === 'habits' ? 'active' : ''}`} onClick={() => { setActiveTab('habits'); setSidebarOpen(false); }}>
                <CheckCircle2 size={18} /> {t("nav.habits")}
                {habits.length > 0 && <Badge variant="secondary">{habits.length}</Badge>}
              </button>
              <button className={`nav-btn ${activeTab === 'metrics' ? 'active' : ''}`} onClick={() => { setActiveTab('metrics'); setSidebarOpen(false); }}>
                <BarChart3 size={18} /> {t("nav.metrics")}
                {customMetrics.length > 0 && <Badge variant="secondary">{customMetrics.length}</Badge>}
              </button>
              <button className={`nav-btn ${activeTab === 'forms' ? 'active' : ''}`} onClick={() => { setActiveTab('forms'); setSidebarOpen(false); }}>
                <ClipboardList size={18} /> {t("nav.forms")}
                {forms.length > 0 && <Badge variant="secondary">{forms.length}</Badge>}
              </button>
            </div>
          </div>

          {/* Komunikacja group */}
          <div className="sidebar-group">
            <div className={`sidebar-group-header ${collapsedGroups.komunikacja ? 'collapsed' : ''}`} onClick={() => setCollapsedGroups(p => ({...p, komunikacja: !p.komunikacja}))}>
              <span>{t("nav.group.communication")}</span>
              <ChevronDown size={14} />
            </div>
            <div className={`sidebar-group-items ${collapsedGroups.komunikacja ? 'collapsed' : ''}`}>
              <button className={`nav-btn ${activeTab === 'files' ? 'active' : ''}`} onClick={() => { setActiveTab('files'); setSidebarOpen(false); }}>
                <Upload size={18} /> {t("nav.files")}
                {sharedFiles.length > 0 && <Badge variant="secondary">{sharedFiles.length}</Badge>}
              </button>
              <button className={`nav-btn ${activeTab === 'videos' ? 'active' : ''}`} onClick={() => { setActiveTab('videos'); setSidebarOpen(false); }}>
                <Video size={18} /> {t("nav.technique")}
                {techniqueVideos.filter(v => v.status === 'pending').length > 0 && (
                  <Badge variant="destructive">{techniqueVideos.filter(v => v.status === 'pending').length}</Badge>
                )}
              </button>
              <button className={`nav-btn ${activeTab === 'alerts' ? 'active' : ''}`} onClick={() => { setActiveTab('alerts'); setSidebarOpen(false); }}>
                <AlertCircle size={18} /> {t("nav.alerts")}
                {alerts.length > 0 && <Badge variant="destructive">{alerts.length}</Badge>}
              </button>
            </div>
          </div>

          {/* Admin group */}
          <div className="sidebar-group">
            <div className={`sidebar-group-header ${collapsedGroups.admin ? 'collapsed' : ''}`} onClick={() => setCollapsedGroups(p => ({...p, admin: !p.admin}))}>
              <span>{t("nav.group.admin")}</span>
              <ChevronDown size={14} />
            </div>
            <div className={`sidebar-group-items ${collapsedGroups.admin ? 'collapsed' : ''}`}>
              {showAdminTools && (
              <button className={`nav-btn ${activeTab === 'payments' ? 'active' : ''}`} onClick={() => { setActiveTab('payments'); setSidebarOpen(false); }}>
                <CreditCard size={18} /> {t("nav.payments")}
                {clientSubscriptions.filter(s => !s.paid && s.status === 'active').length > 0 && (
                  <Badge variant="destructive">{clientSubscriptions.filter(s => !s.paid && s.status === 'active').length}</Badge>
                )}
              </button>
              )}
              <button className={`nav-btn ${activeTab === 'finance' ? 'active' : ''}`} onClick={() => { setActiveTab('finance'); setSidebarOpen(false); }}>
                <Wallet size={18} /> Finanse
              </button>
              <button className={`nav-btn ${activeTab === 'analytics' ? 'active' : ''}`} onClick={() => { setActiveTab('analytics'); setSidebarOpen(false); }}>
                <TrendingUp size={18} /> {t("nav.analytics")}
              </button>
              <button className={`nav-btn ${activeTab === 'automations' ? 'active' : ''}`} onClick={() => { setActiveTab('automations'); setSidebarOpen(false); }}>
                <Zap size={18} /> {t("nav.automations")}
                {automations.length > 0 && <Badge variant="secondary">{automations.length}</Badge>}
              </button>
              {showAdminTools && (
              <button className={`nav-btn ${activeTab === 'activity' ? 'active' : ''}`} onClick={() => { setActiveTab('activity'); setSidebarOpen(false); }}>
                <Activity size={18} /> {t("nav.activity")}
              </button>
              )}
              {showFirmPanel && (
              <button className={`nav-btn ${activeTab === 'organization' ? 'active' : ''}`} onClick={() => { setActiveTab('organization'); setSidebarOpen(false); }}>
                <Building2 size={18} /> Firma
              </button>
              )}
              {showAdminTools && (
              <button className={`nav-btn ${activeTab === 'config' ? 'active' : ''}`} onClick={() => { setActiveTab('config'); setSidebarOpen(false); }}>
                <SlidersHorizontal size={18} /> {t("nav.config")}
              </button>
              )}
              <button className={`nav-btn ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => { setActiveTab('settings'); setSidebarOpen(false); }}>
                <Settings size={18} /> Ustawienia konta
              </button>
            </div>
          </div>
        </nav>

        <div className="sidebar-footer">
          <button type="button" onClick={() => { setActiveTab('settings'); setSidebarOpen(false); }}
            title="Ustawienia konta"
            style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '8px',
              marginBottom: 8, border: 'none', background: 'transparent', cursor: 'pointer',
              borderRadius: 10, color: 'var(--text-primary)', textAlign: 'left' }}>
            <span style={{ width: 38, height: 38, borderRadius: '50%', flexShrink: 0, overflow: 'hidden',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff', fontSize: 14, fontWeight: 700 }}>
              {user.avatar_url
                ? <img src={user.avatar_url.startsWith('http') ? user.avatar_url : `${BACKEND_URL}${user.avatar_url}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : (user.name || '?').trim().split(/\s+/).map(w => w[0]).slice(0,2).join('').toUpperCase()}
            </span>
            <span style={{ overflow: 'hidden' }}>
              <span style={{ display: 'block', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>{user.name}</span>
              <span style={{ display: 'block', fontSize: 11, color: 'var(--text-secondary)' }}>Ustawienia konta</span>
            </span>
          </button>
          <div className="trainer-code" onClick={copyCode} data-testid="trainer-code">
            <span>Twój kod</span>
            <code>{user.trainer_code}</code>
            <Copy size={14} />
          </div>
          <div className="sidebar-footer-actions">
            {canBroadcast && (
              <Button variant="ghost" title="Ogłoszenie do zespołu" onClick={() => setBroadcastOpen(true)} data-testid="broadcast-btn">
                <Megaphone size={18} />
              </Button>
            )}
            <Button variant="ghost" className="notif-bell sidebar-bell" onClick={openNotifications} data-testid="trainer-notif-bell">
              <Bell size={18} />
              {unreadCount > 0 && <span className="notif-badge">{unreadCount}</span>}
            </Button>
            <Button variant="ghost" className="logout-btn" onClick={() => { logout(); navigate("/"); }}>
              <LogOut size={18} /> Wyloguj
            </Button>
          </div>
        </div>
      </aside>

      <main className="trainer-main">
        <GuestBanner />
        {activeTab === 'clients' && (
          <ClientsTab
            clients={clients}
            collapsedGroups={collapsedGroups}
            exercises={exercises}
            filteredClients={filteredClients}
            openClientEdit={openClientEdit}
            removeClient={removeClient}
            searchClients={searchClients}
            setAiWorkoutNotes={setAiWorkoutNotes}
            setAiWorkoutOpen={setAiWorkoutOpen}
            setAiWorkoutPlan={setAiWorkoutPlan}
            setCollapsedGroups={setCollapsedGroups}
            setPlanOpen={setPlanOpen}
            setSearchClients={setSearchClients}
            connectClientByCode={connectClientByCode}
            createManagedClient={createManagedClient}
            inviteManagedClient={inviteManagedClient}
            plan={plan}
            teamData={teamData}
            user={user}
            clientsView={clientsView}
            setClientsView={setClientsView}
            clientTrainerFilter={clientTrainerFilter}
            setClientTrainerFilter={setClientTrainerFilter}
            clientLocationFilter={clientLocationFilter}
            setClientLocationFilter={setClientLocationFilter}
            clientTrainerOptions={clientTrainerOptions}
            firmLocations={firmLocations}
          />
        )}

        {activeTab === 'exercises' && (
          <ExercisesTab
            deleteExercise={deleteExercise}
            filteredExercises={filteredExercises}
            searchExercises={searchExercises}
            setEditingExercise={setEditingExercise}
            setExerciseOpen={setExerciseOpen}
            setGifPreview={setGifPreview}
            setNewExercise={setNewExercise}
            setSearchExercises={setSearchExercises}
            equipmentFilter={equipmentFilter}
            setEquipmentFilter={setEquipmentFilter}
            equipmentOptions={exerciseEquipmentOptions}
            muscleGroupFilter={muscleGroupFilter}
            setMuscleGroupFilter={setMuscleGroupFilter}
            muscleGroupOptions={exerciseMuscleGroupOptions}
            levelFilter={levelFilter}
            setLevelFilter={setLevelFilter}
            levelOptions={exerciseLevelOptions}
            mechanicFilter={mechanicFilter}
            setMechanicFilter={setMechanicFilter}
            mechanicOptions={exerciseMechanicOptions}
          />
        )}

        {activeTab === 'habits' && (
          <HabitsTab
            deleteHabit={deleteHabit}
            filteredHabits={filteredHabits}
            searchHabits={searchHabits}
            setEditingHabit={setEditingHabit}
            setHabitOpen={setHabitOpen}
            setNewHabit={setNewHabit}
            setSearchHabits={setSearchHabits}
          />
        )}

        {activeTab === 'metrics' && (
          <MetricsTab
            customMetrics={customMetrics}
            deleteMetric={deleteMetric}
            metricIconMap={metricIconMap}
            openMetricEdit={openMetricEdit}
            setEditingMetric={setEditingMetric}
            setMetricOpen={setMetricOpen}
            setNewMetric={setNewMetric}
          />
        )}

        {activeTab === 'forms' && (
          <FormsTab
            clients={clients}
            deleteForm={deleteForm}
            filteredForms={filteredForms}
            formResponses={formResponses}
            forms={forms}
            searchForms={searchForms}
            setAssignData={setAssignData}
            setAssignFormOpen={setAssignFormOpen}
            setEditingForm={setEditingForm}
            setFormBuilderOpen={setFormBuilderOpen}
            setNewForm={setNewForm}
            setSearchForms={setSearchForms}
            viewFormResponses={viewFormResponses}
          />
        )}

        {activeTab === 'automations' && (
          <AutomationsTab
            actionLabels={actionLabels}
            automationLogs={automationLogs}
            automations={automations}
            deleteAutomation={deleteAutomation}
            setAutomationOpen={setAutomationOpen}
            setEditingAutomation={setEditingAutomation}
            setNewAutomation={setNewAutomation}
            toggleAutomation={toggleAutomation}
            triggerLabels={triggerLabels}
          />
        )}

        {activeTab === 'templates' && (
          <TemplatesTab
            clients={clients}
            deleteTemplate={deleteTemplate}
            programTemplates={programTemplates}
            setAssignTemplateData={setAssignTemplateData}
            setAssignTemplateOpen={setAssignTemplateOpen}
            setEditingTemplate={setEditingTemplate}
            setNewTemplate={setNewTemplate}
            setTemplateOpen={setTemplateOpen}
            templateCategoryLabels={templateCategoryLabels}
          />
        )}

        {activeTab === 'nutrition' && (
          <NutritionTab
            clients={clients}
            deleteMealPlan={deleteMealPlan}
            mealPlans={mealPlans}
            setMealPlanOpen={setMealPlanOpen}
            setNewMealPlan={setNewMealPlan}
          />
        )}

        {activeTab === 'analytics' && analytics && (
          <AnalyticsTab
            analytics={analytics}
            ratingSummary={ratingSummary}
          />
        )}

        {activeTab === 'calendar' && (
          <CalendarTab
            availabilityExceptions={availabilityExceptions}
            availabilitySlots={availabilitySlots}
            calendarScope={calendarScope}
            calendarSubTab={calendarSubTab}
            dayLabels={dayLabels}
            deleteAvailabilitySlot={deleteAvailabilitySlot}
            deleteException={deleteException}
            deleteLeave={deleteLeave}
            deleteSession={deleteSession}
            filteredSessions={filteredSessions}
            firmLocations={firmLocations}
            handleCalendarAction={handleCalendarAction}
            isBusinessOrg={isBusinessOrg}
            leaveSummary={leaveSummary}
            leaveTypeLabels={leaveTypeLabels}
            openNewSession={openNewSession}
            openReassign={openReassign}
            searchSessions={searchSessions}
            sessionTypeLabels={sessionTypeLabels}
            sessions={sessions}
            sessionsView={sessionsView}
            setAvailSlotOpen={setAvailSlotOpen}
            setCalendarScope={setCalendarScope}
            setCalendarSubTab={setCalendarSubTab}
            setExceptionOpen={setExceptionOpen}
            setLeaveOpen={setLeaveOpen}
            setNewAvailSlot={setNewAvailSlot}
            setNewException={setNewException}
            setNewLeave={setNewLeave}
            setSearchSessions={setSearchSessions}
            setSessionsView={setSessionsView}
            t={t}
            token={token}
            trainerLeaves={trainerLeaves}
            sessionAction={sessionAction}
            rejectSession={rejectSession}
            cancelSession={cancelSession}
            markAbsent={markAbsent}
          />
        )}

        {activeTab === 'videos' && (
          <VideosTab
            setSelectedVideo={setSelectedVideo}
            setVideoReviewOpen={setVideoReviewOpen}
            techniqueVideos={techniqueVideos}
          />
        )}

        {activeTab === 'alerts' && (
          <AlertsTab
            alerts={alerts}
            clients={clients}
            openClientEdit={openClientEdit}
          />
        )}

        {activeTab === 'team' && (() => {
          const renderTreeNode = (node, depth = 0, isLast = true, prefix = '') => {
            const connector = depth === 0 ? '' : (isLast ? '└── ' : '├── ');
            const childPrefix = depth === 0 ? '' : (isLast ? '    ' : '│   ');
            return (
              <div key={node.id}>
                <div className="tree-node" style={{paddingLeft: depth > 0 ? `${(depth - 1) * 28 + 8}px` : '0'}}>
                  {depth > 0 && <span className="tree-connector">{prefix}{connector}</span>}
                  <div className={`tree-card clickable-card ${depth === 0 ? 'tree-root' : ''}`} onClick={() => { setActiveTab('clients'); toast.info(`Klienci trenera: ${node.name}`); }}>
                    <div className="tree-avatar" style={{background: depth === 0 ? 'var(--primary)' : depth === 1 ? '#6366f1' : '#8b5cf6'}}>
                      {node.name?.charAt(0)}
                    </div>
                    <div className="tree-info">
                      <h4>{node.name}</h4>
                      {node.email && <p className="tree-email">{node.email}</p>}
                    </div>
                    <div className="tree-stats">
                      <span className="tree-stat">{node.client_count} kl.</span>
                      {node.total_clients > node.client_count && (
                        <span className="tree-stat total">({node.total_clients} total)</span>
                      )}
                      {depth === 1 && (
                        <Button variant="ghost" size="sm" title="Usuń z zespołu"
                                data-testid={`remove-from-team-${node.id}`}
                                onClick={(e) => { e.stopPropagation(); removeFromTeam(node); }}>
                          <UserMinus size={15} />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
                {(node.sub_trainers || []).map((child, i) =>
                  renderTreeNode(child, depth + 1, i === node.sub_trainers.length - 1, prefix + childPrefix)
                )}
              </div>
            );
          };

          const me = teamData.me || {};
          // Only the trainers below this one. For a firm member the endpoint
          // returns the whole company rooted at its owner, which would show a
          // junior trainer their manager and peers under "Struktura zespołu"
          // and never render the empty state they should see.
          const tree = ownSubTree(teamData.tree || [], user.id);

          return (
          <div className="team-view">
            <div className="view-header">
              <h1>Zespół</h1>
              <div style={{display: 'flex', gap: '8px'}}>
                {user.parent_trainer_id && (
                  <Button variant="outline" onClick={async () => {
                    if (window.confirm("Na pewno chcesz opuścić zespół?")) {
                      try {
                        await api("delete", "/trainer/leave-team", null, token);
                        toast.success("Opuszczono zespół");
                        fetchData();
                        window.location.reload();
                      } catch (err) { toast.error(err.response?.data?.detail || "Błąd"); }
                    }
                  }}>
                    <LogOut size={16} /> Opuść zespół
                  </Button>
                )}
                <Button onClick={() => setJoinTeamOpen(true)}>
                  <Plus size={16} /> Dołącz do zespołu
                </Button>
              </div>
            </div>

            {teamData.parent_trainer && (
              <Card className="clickable-card" style={{marginBottom: '16px', borderLeft: '4px solid var(--primary)', cursor: 'pointer'}} onClick={() => toast.info(`Przełożony: ${teamData.parent_trainer.name} (${teamData.parent_trainer.email || ''})`)}>
                <CardContent style={{display: 'flex', alignItems: 'center', gap: '12px', padding: '16px'}}>
                  <div style={{width: '36px', height: '36px', borderRadius: '50%', background: 'var(--primary)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: '0.9rem'}}>
                    {teamData.parent_trainer.name?.charAt(0)}
                  </div>
                  <div>
                    <p style={{margin: 0, fontSize: '0.8rem', color: 'var(--muted-foreground)'}}>Przełożony</p>
                    <h3 style={{margin: 0, fontSize: '0.95rem'}}>{teamData.parent_trainer.name}</h3>
                  </div>
                </CardContent>
              </Card>
            )}

            <div style={{marginBottom: '20px', padding: '14px 18px', background: 'var(--secondary)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
              <div>
                <p style={{margin: 0, fontSize: '0.85rem', fontWeight: 500}}>Twój kod zespołowy</p>
                <p style={{margin: 0, fontSize: '0.75rem', color: 'var(--muted-foreground)'}}>Udostępnij trenerom, aby dołączyli</p>
              </div>
              <code style={{fontSize: '1.2rem', fontWeight: 600, letterSpacing: '2px', cursor: 'pointer'}} onClick={copyCode}>{user.trainer_code}</code>
            </div>

            {tree.length === 0 ? (
              <Card className="empty-state">
                <CardContent>
                  <Users size={48} strokeWidth={1} />
                  <h3>Brak trenerów w zespole</h3>
                  <p>Udostępnij swój kod zespołowy innym trenerom</p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent style={{padding: '24px'}}>
                  <h3 style={{margin: '0 0 20px 0', fontSize: '0.95rem', color: 'var(--muted-foreground)'}}>Struktura zespołu</h3>
                  <div className="team-tree">
                    {renderTreeNode({
                      id: me.id,
                      name: me.name + ' (Ty)',
                      client_count: me.client_count || 0,
                      total_clients: me.total_clients || 0,
                      sub_trainers: tree
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
          );
        })()}

        {activeTab === 'groups' && (
          <GroupsTab
            clientGroups={clientGroups}
            clients={clients}
            deleteGroup={deleteGroup}
            setEditingGroup={setEditingGroup}
            setGroupMessageOpen={setGroupMessageOpen}
            setGroupMessageTarget={setGroupMessageTarget}
            setGroupMessageText={setGroupMessageText}
            setGroupOpen={setGroupOpen}
            setNewGroup={setNewGroup}
          />
        )}

        {activeTab === 'files' && (
          <FilesTab
            clients={clients}
            deleteSharedFile={deleteSharedFile}
            fileTypeIcons={fileTypeIcons}
            setFileShareOpen={setFileShareOpen}
            setNewSharedFile={setNewSharedFile}
            sharedFiles={sharedFiles}
          />
        )}

        {activeTab === 'payments' && (
          <PaymentsTab
            clientSubscriptions={clientSubscriptions}
            clients={clients}
            exportFinancialCSV={exportFinancialCSV}
            kpiData={kpiData}
            paymentMethodLabels={paymentMethodLabels}
            paymentSummary={paymentSummary}
            payments={payments}
            setAssignSubData={setAssignSubData}
            setAssignSubOpen={setAssignSubOpen}
            setNewPlanData={setNewPlanData}
            setPaymentData={setPaymentData}
            setPaymentDialogOpen={setPaymentDialogOpen}
            setPaymentTarget={setPaymentTarget}
            setSubPlanOpen={setSubPlanOpen}
            subscriptionPlans={subscriptionPlans}
            deleteSubscriptionPlan={deleteSubscriptionPlan}
            t={t}
          />
        )}

        {activeTab === 'finance' && (
          <FinanceTab token={token} clients={clients} currentUserId={user?.id} />
        )}

        {/* ===== RUNNING TAB (TRAINER) ===== */}
        {activeTab === 'running' && (
          <RunningTab
            deleteTrainerRunningPlan={deleteTrainerRunningPlan}
            setAssignRunningPlanOpen={setAssignRunningPlanOpen}
            setNewTrainerRunningPlan={setNewTrainerRunningPlan}
            setTrainerRunningPlanOpen={setTrainerRunningPlanOpen}
            trainerDifficultyLabels={trainerDifficultyLabels}
            trainerFormatRunTime={trainerFormatRunTime}
            trainerIntervalTypeNames={trainerIntervalTypeNames}
            trainerRunningPlans={trainerRunningPlans}
          />
        )}
        {activeTab === 'activity' && (
          <ActivityTab
            auditLogs={auditLogs}
          />
        )}
        {activeTab === 'diet' && (
          <Suspense fallback={<div style={{padding:'32px',color:'var(--text-secondary)'}}>Ladowanie...</div>}>
            <DietGenerator token={token} />
          </Suspense>
        )}
        {activeTab === 'recipes' && (
          <Suspense fallback={<div style={{padding:'32px',color:'var(--text-secondary)'}}>Ladowanie...</div>}>
            <RecipeEditor token={token} />
          </Suspense>
        )}
        {activeTab === 'organization' && (
          <Suspense fallback={<div style={{padding:'32px',color:'var(--text-secondary)'}}>Ladowanie...</div>}>
            <OrganizationPanel token={token} />
          </Suspense>
        )}
        {activeTab === 'config' && (
          <Suspense fallback={<div style={{padding:'32px',color:'var(--text-secondary)'}}>Ladowanie konfiguracji...</div>}>
            <AdminConfigPanel token={token} />
          </Suspense>
        )}
        {activeTab === 'settings' && (
          <Suspense fallback={<div style={{padding:'32px',color:'var(--text-secondary)'}}>Ladowanie...</div>}>
            <TrainerSettings user={user} token={token} onUserUpdate={updateUser} />
          </Suspense>
        )}
      </main>

      {/* Create Running Plan Dialog (Trainer) */}
      <Dialog open={trainerRunningPlanOpen} onOpenChange={setTrainerRunningPlanOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Nowy plan Cardio</DialogTitle></DialogHeader>
          <ScrollArea className="max-h-[70vh]">
            <div className="dialog-form">
              <div className="field"><Label>Typ aktywności</Label>
                <Select value={newTrainerRunningPlan.activity_type || "running"} onValueChange={(v) => setNewTrainerRunningPlan(p => ({...p, activity_type: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="running">Bieganie</SelectItem>
                    <SelectItem value="treadmill">Bieżnia</SelectItem>
                    <SelectItem value="ergometer">Ergometr</SelectItem>
                    <SelectItem value="nordic_walking">Nordic Walking</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="field"><Label>Nazwa</Label><Input value={newTrainerRunningPlan.name} onChange={(e) => setNewTrainerRunningPlan(p => ({...p, name: e.target.value}))} /></div>
              <div className="field"><Label>Opis</Label><Textarea value={newTrainerRunningPlan.description} onChange={(e) => setNewTrainerRunningPlan(p => ({...p, description: e.target.value}))} /></div>
              <div className="field"><Label>Cel</Label><Input value={newTrainerRunningPlan.goal} onChange={(e) => setNewTrainerRunningPlan(p => ({...p, goal: e.target.value}))} placeholder="np. 5km poniżej 25 min" /></div>
              <div className="field"><Label>Trudność</Label>
                <Select value={newTrainerRunningPlan.difficulty} onValueChange={(v) => setNewTrainerRunningPlan(p => ({...p, difficulty: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="easy">Łatwy</SelectItem>
                    <SelectItem value="medium">Średni</SelectItem>
                    <SelectItem value="hard">Trudny</SelectItem>
                    <SelectItem value="expert">Ekspert</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="fields-row">
                <div className="field"><Label>Dystans (km)</Label><Input type="number" step="0.1" value={newTrainerRunningPlan.estimated_distance_km} onChange={(e) => setNewTrainerRunningPlan(p => ({...p, estimated_distance_km: e.target.value}))} /></div>
                <div className="field"><Label>Czas (min)</Label><Input type="number" value={newTrainerRunningPlan.estimated_duration_minutes} onChange={(e) => setNewTrainerRunningPlan(p => ({...p, estimated_duration_minutes: e.target.value}))} /></div>
              </div>

              <h4>Interwały ({newTrainerRunningPlan.intervals.length})</h4>
              {newTrainerRunningPlan.intervals.map((interval, idx) => (
                <div key={idx} className={`interval-editor-item interval-${interval.type}`}>
                  <span>{idx + 1}. {trainerIntervalTypeNames[interval.type]} - {trainerFormatRunTime(interval.duration_seconds)}</span>
                  {interval.target_pace_min && <span className="interval-pace-sm"> ({interval.target_pace_min}-{interval.target_pace_max} min/km)</span>}
                  <Button variant="ghost" size="sm" onClick={() => removeTrainerInterval(idx)}><Trash2 size={14} /></Button>
                </div>
              ))}

              <Card className="add-interval-card">
                <CardContent>
                  <div className="interval-form-grid">
                    <div className="field"><Label>Typ</Label>
                      <Select value={newTrainerInterval.type} onValueChange={(v) => setNewTrainerInterval(p => ({...p, type: v}))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="warmup">Rozgrzewka</SelectItem>
                          <SelectItem value="run">Bieg</SelectItem>
                          <SelectItem value="sprint">Sprint</SelectItem>
                          <SelectItem value="recovery">Odpoczynek</SelectItem>
                          <SelectItem value="walk">Marsz</SelectItem>
                          <SelectItem value="cooldown">Schładzanie</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="field"><Label>Czas (s)</Label><Input type="number" value={newTrainerInterval.duration_seconds} onChange={(e) => setNewTrainerInterval(p => ({...p, duration_seconds: e.target.value}))} /></div>
                    <div className="field"><Label>Tempo min</Label><Input type="number" step="0.1" value={newTrainerInterval.target_pace_min} onChange={(e) => setNewTrainerInterval(p => ({...p, target_pace_min: e.target.value}))} /></div>
                    <div className="field"><Label>Tempo max</Label><Input type="number" step="0.1" value={newTrainerInterval.target_pace_max} onChange={(e) => setNewTrainerInterval(p => ({...p, target_pace_max: e.target.value}))} /></div>
                  </div>
                  <Button variant="outline" size="sm" onClick={addTrainerInterval} className="mt-2"><Plus size={14} /> Dodaj</Button>
                </CardContent>
              </Card>
            </div>
          </ScrollArea>
          <DialogFooter><Button onClick={saveTrainerRunningPlan}><Save size={16} /> Zapisz plan</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Running Plan Dialog */}
      <Dialog open={assignRunningPlanOpen} onOpenChange={setAssignRunningPlanOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Przypisz plan biegowy</DialogTitle></DialogHeader>
          <div className="field"><Label>Plan</Label>
            <Select value={assignRunningPlanId} onValueChange={setAssignRunningPlanId}>
              <SelectTrigger><SelectValue placeholder="Wybierz plan..." /></SelectTrigger>
              <SelectContent>
                {trainerRunningPlans.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="field"><Label>Klient</Label>
            <Select value={assignRunningClientId} onValueChange={setAssignRunningClientId}>
              <SelectTrigger><SelectValue placeholder="Wybierz klienta..." /></SelectTrigger>
              <SelectContent>
                {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter><Button onClick={assignRunningPlan}><UserPlus size={16} /> Przypisz</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Join Team Dialog */}
      <Dialog open={joinTeamOpen} onOpenChange={setJoinTeamOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Dołącz do zespołu</DialogTitle>
          </DialogHeader>
          <div className="dialog-form">
            <div className="field">
              <Label>Kod zespołowy trenera</Label>
              <Input value={joinTeamCode} onChange={(e) => setJoinTeamCode(e.target.value.toUpperCase())} placeholder="np. A1B2C3D4" maxLength={8} />
            </div>
            <Button onClick={async () => {
              try {
                const res = await api("post", "/trainer/join-team", { trainer_code: joinTeamCode }, token);
                toast.success(res.data.message || "Dołączono do zespołu!");
                setJoinTeamOpen(false);
                setJoinTeamCode("");
                fetchData();
                window.location.reload();
              } catch (err) {
                toast.error(err.response?.data?.detail || "Nie udało się dołączyć");
              }
            }} disabled={joinTeamCode.length < 8}>
              Dołącz
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Group Dialog */}
      <Dialog open={groupOpen} onOpenChange={(open) => { setGroupOpen(open); if (!open) setEditingGroup(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingGroup ? 'Edytuj grupę' : 'Nowa grupa'}</DialogTitle>
            <DialogDescription>Utwórz grupę klientów dla wspólnych planów</DialogDescription>
          </DialogHeader>
          <div className="dialog-form">
            <div className="field">
              <Label>Nazwa grupy</Label>
              <Input value={newGroup.name} onChange={(e) => setNewGroup({...newGroup, name: e.target.value})} placeholder="np. Grupa poranna" />
            </div>
            <div className="field">
              <Label>Opis <span className="optional">({t("general.optional")})</span></Label>
              <Input value={newGroup.description} onChange={(e) => setNewGroup({...newGroup, description: e.target.value})} placeholder="Krótki opis grupy" />
            </div>
            <div className="field">
              <Label>Lokalizacja <span className="optional">({t("general.optional")})</span></Label>
              <Input value={newGroup.location} onChange={(e) => setNewGroup({...newGroup, location: e.target.value})} placeholder="np. Siłownia Centrum, Sala 3" />
            </div>
            <div className="field">
              <Label>Członkowie</Label>
              <div className="group-member-picker">
                {flatClients(clients).map(c => (
                  <label key={c.id} className="group-member-option">
                    <Checkbox
                      checked={newGroup.client_ids.includes(c.id)}
                      onCheckedChange={(checked) => {
                        setNewGroup({
                          ...newGroup,
                          client_ids: checked
                            ? [...newGroup.client_ids, c.id]
                            : newGroup.client_ids.filter(id => id !== c.id)
                        });
                      }}
                    />
                    <span>{c.name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGroupOpen(false)}>Anuluj</Button>
            <Button onClick={createGroup} disabled={!newGroup.name.trim()}>
              {editingGroup ? 'Zapisz' : 'Utwórz'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Group Message Dialog */}
      <Dialog open={groupMessageOpen} onOpenChange={setGroupMessageOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Wiadomość do grupy</DialogTitle>
            <DialogDescription>Wyślij do: {groupMessageTarget?.name} ({groupMessageTarget?.client_ids?.length} członków)</DialogDescription>
          </DialogHeader>
          <div className="dialog-form">
            <div className="field">
              <Label>Treść wiadomości</Label>
              <Textarea
                value={groupMessageText}
                onChange={(e) => setGroupMessageText(e.target.value)}
                placeholder="Wpisz wiadomość do grupy..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGroupMessageOpen(false)}>Anuluj</Button>
            <Button onClick={sendGroupMessage} disabled={!groupMessageText.trim()}>
              <Send size={14} /> Wyślij
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share File Dialog */}
      <Dialog open={fileShareOpen} onOpenChange={setFileShareOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Udostępnij plik</DialogTitle>
            <DialogDescription>Dodaj link do pliku dla klientów</DialogDescription>
          </DialogHeader>
          <div className="dialog-form">
            <div className="field">
              <Label>Nazwa pliku</Label>
              <Input value={newSharedFile.name} onChange={(e) => setNewSharedFile({...newSharedFile, name: e.target.value})} placeholder="np. Plan treningowy" />
            </div>
            <div className="field">
              <Label>URL pliku</Label>
              <Input value={newSharedFile.url} onChange={(e) => setNewSharedFile({...newSharedFile, url: e.target.value})} placeholder="https://..." />
            </div>
            <div className="field">
              <Label>Typ pliku</Label>
              <Select value={newSharedFile.file_type} onValueChange={(v) => setNewSharedFile({...newSharedFile, file_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pdf">PDF</SelectItem>
                  <SelectItem value="image">Obraz</SelectItem>
                  <SelectItem value="document">Dokument</SelectItem>
                  <SelectItem value="video">Video</SelectItem>
                  <SelectItem value="other">Inny</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="field">
              <Label>Udostępnij dla</Label>
              <Select value={newSharedFile.client_id} onValueChange={(v) => setNewSharedFile({...newSharedFile, client_id: v})}>
                <SelectTrigger><SelectValue placeholder="Wszyscy klienci" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Wszyscy klienci</SelectItem>
                  {flatClients(clients).map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="field">
              <Label>Notatka <span className="optional">(opcjonalnie)</span></Label>
              <Input value={newSharedFile.notes} onChange={(e) => setNewSharedFile({...newSharedFile, notes: e.target.value})} placeholder="Krótki opis pliku" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFileShareOpen(false)}>Anuluj</Button>
            <Button onClick={createSharedFile} disabled={!newSharedFile.name.trim() || !newSharedFile.url.trim()}>
              <Upload size={14} /> Udostępnij
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Subscription Plan Dialog */}
      <Dialog open={subPlanOpen} onOpenChange={setSubPlanOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nowy pakiet</DialogTitle>
            <DialogDescription>Utwórz pakiet subskrypcji</DialogDescription>
          </DialogHeader>
          <div className="dialog-form">
            <div className="field">
              <Label>Nazwa pakietu</Label>
              <Input value={newPlanData.name} onChange={(e) => setNewPlanData({...newPlanData, name: e.target.value})} placeholder="np. Trening personalny" />
            </div>
            <div className="field">
              <Label>Cena (PLN)</Label>
              <Input type="number" value={newPlanData.price} onChange={(e) => setNewPlanData({...newPlanData, price: e.target.value})} placeholder="299" />
            </div>
            <div className="field">
              <Label>Czas trwania (dni)</Label>
              <Input type="number" value={newPlanData.duration_days} onChange={(e) => setNewPlanData({...newPlanData, duration_days: e.target.value})} />
            </div>
            <div className="field">
              <Label>Opis</Label>
              <Input value={newPlanData.description} onChange={(e) => setNewPlanData({...newPlanData, description: e.target.value})} placeholder="Krótki opis pakietu" />
            </div>
            <div className="field">
              <Label>Cechy (jedna na linię)</Label>
              <Textarea value={newPlanData.features} onChange={(e) => setNewPlanData({...newPlanData, features: e.target.value})} placeholder={"Plan treningowy\nCzat z trenerem\nCheck-in"} rows={4} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubPlanOpen(false)}>Anuluj</Button>
            <Button onClick={createSubPlan} disabled={!newPlanData.name.trim() || !newPlanData.price}>Utwórz</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Subscription Dialog */}
      <Dialog open={assignSubOpen} onOpenChange={setAssignSubOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Przypisz pakiet</DialogTitle>
            <DialogDescription>Przypisz subskrypcję do klienta</DialogDescription>
          </DialogHeader>
          <div className="dialog-form">
            <div className="field">
              <Label>Klient</Label>
              <Select value={assignSubData.client_id} onValueChange={(v) => setAssignSubData({...assignSubData, client_id: v})}>
                <SelectTrigger><SelectValue placeholder="Wybierz klienta" /></SelectTrigger>
                <SelectContent>
                  {flatClients(clients).map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="field">
              <Label>Pakiet</Label>
              <Select value={assignSubData.plan_id} onValueChange={(v) => setAssignSubData({...assignSubData, plan_id: v})}>
                <SelectTrigger><SelectValue placeholder="Wybierz pakiet" /></SelectTrigger>
                <SelectContent>
                  {subscriptionPlans.filter(p => p.active).map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name} - {p.price} PLN</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {firmLocations.length > 0 && (
              <div className="field">
                <Label>Lokalizacja <span className="optional">(opcjonalnie)</span></Label>
                <Select value={assignSubData.location_id} onValueChange={(v) => setAssignSubData({...assignSubData, location_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Bez przypisania" /></SelectTrigger>
                  <SelectContent>
                    {firmLocations.map(l => (
                      <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignSubOpen(false)}>Anuluj</Button>
            <Button onClick={assignSubscription} disabled={!assignSubData.client_id || !assignSubData.plan_id}>Przypisz</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Zapisz płatność</DialogTitle>
            <DialogDescription>{paymentTarget?.plan_name}</DialogDescription>
          </DialogHeader>
          <div className="dialog-form">
            <div className="field">
              <Label>Kwota (PLN)</Label>
              <Input type="number" value={paymentData.amount} onChange={(e) => setPaymentData({...paymentData, amount: e.target.value})} />
            </div>
            <div className="field">
              <Label>Metoda płatności</Label>
              <Select value={paymentData.method} onValueChange={(v) => setPaymentData({...paymentData, method: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="transfer">Przelew</SelectItem>
                  <SelectItem value="cash">Gotówka</SelectItem>
                  <SelectItem value="card">Karta</SelectItem>
                  <SelectItem value="manual">Ręcznie</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="field">
              <Label>Notatka <span className="optional">(opcjonalnie)</span></Label>
              <Input value={paymentData.notes} onChange={(e) => setPaymentData({...paymentData, notes: e.target.value})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentDialogOpen(false)}>Anuluj</Button>
            <Button onClick={recordPayment} disabled={!paymentData.amount}>
              <Check size={14} /> Zapisz
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Metric Dialog */}
      <Dialog open={metricOpen} onOpenChange={(open) => { setMetricOpen(open); if (!open) setEditingMetric(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingMetric ? 'Edytuj metrykę' : 'Nowa metryka'}</DialogTitle>
          </DialogHeader>
          <div className="dialog-form">
            <div className="field">
              <Label>Nazwa</Label>
              <Input value={newMetric.name} onChange={(e) => setNewMetric({...newMetric, name: e.target.value})} placeholder="np. Tętno spoczynkowe" />
            </div>
            <div className="field">
              <Label>Jednostka</Label>
              <Input value={newMetric.unit} onChange={(e) => setNewMetric({...newMetric, unit: e.target.value})} placeholder="np. bpm, kg, h" />
            </div>
            <div className="field-row">
              <div className="field">
                <Label>Min <span className="optional">(opcjonalne)</span></Label>
                <Input type="number" value={newMetric.min_value} onChange={(e) => setNewMetric({...newMetric, min_value: e.target.value})} placeholder="0" />
              </div>
              <div className="field">
                <Label>Max <span className="optional">(opcjonalne)</span></Label>
                <Input type="number" value={newMetric.max_value} onChange={(e) => setNewMetric({...newMetric, max_value: e.target.value})} placeholder="100" />
              </div>
            </div>
            <div className="field">
              <Label>Ikona</Label>
              <div className="icon-picker">
                {Object.entries(metricIconMap).map(([name, Icon]) => (
                  <button key={name} type="button" className={`icon-option ${newMetric.icon === name ? 'active' : ''}`} onClick={() => setNewMetric({...newMetric, icon: name})}>
                    <Icon size={20} />
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setMetricOpen(false); setEditingMetric(null); }}>Anuluj</Button>
            <Button onClick={createMetric} disabled={!newMetric.name || !newMetric.unit}>{editingMetric ? 'Zapisz' : 'Utwórz'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Form Builder Dialog */}
      <Dialog open={formBuilderOpen} onOpenChange={(open) => { setFormBuilderOpen(open); if (!open) setEditingForm(null); }}>
        <DialogContent className="form-builder-dialog">
          <DialogHeader>
            <DialogTitle>{editingForm ? 'Edytuj formularz' : 'Nowy formularz'}</DialogTitle>
          </DialogHeader>
          <ScrollArea className="form-builder-scroll">
            <div className="dialog-form">
              <div className="field">
                <Label>Nazwa formularza</Label>
                <Input value={newForm.name} onChange={(e) => setNewForm({...newForm, name: e.target.value})} placeholder="np. Cotygodniowy check-in" />
              </div>
              <div className="field">
                <Label>Typ</Label>
                <Select value={newForm.type} onValueChange={(v) => setNewForm({...newForm, type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="onboarding">Onboarding</SelectItem>
                    <SelectItem value="check_in">Check-in</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="form-fields-section">
                <Label>Pola formularza</Label>
                {newForm.fields.map((field, i) => (
                  <div key={i} className="form-field-item">
                    <div className="form-field-header">
                      <span className="form-field-number">#{i + 1}</span>
                      <Button variant="ghost" size="sm" onClick={() => removeFormField(i)}><Trash2 size={14} /></Button>
                    </div>
                    <Input value={field.label} onChange={(e) => updateFormField(i, { label: e.target.value, name: `field_${i + 1}` })} placeholder="Pytanie / etykieta" />
                    <div className="form-field-options">
                      <Select value={field.type} onValueChange={(v) => updateFormField(i, { type: v })}>
                        <SelectTrigger className="field-type-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="text">Tekst</SelectItem>
                          <SelectItem value="number">Liczba</SelectItem>
                          <SelectItem value="scale">Skala 1-10</SelectItem>
                          <SelectItem value="yesno">Tak / Nie</SelectItem>
                          <SelectItem value="textarea">Długi tekst</SelectItem>
                        </SelectContent>
                      </Select>
                      <label className="form-field-required">
                        <Checkbox checked={field.required} onCheckedChange={(v) => updateFormField(i, { required: v })} />
                        <span>Wymagane</span>
                      </label>
                    </div>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addFormField} className="add-field-btn">
                  <Plus size={14} /> Dodaj pole
                </Button>
              </div>
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setFormBuilderOpen(false); setEditingForm(null); }}>Anuluj</Button>
            <Button onClick={createForm} disabled={!newForm.name || newForm.fields.length === 0}>{editingForm ? 'Zapisz' : 'Utwórz'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Form Dialog */}
      <Dialog open={assignFormOpen} onOpenChange={setAssignFormOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Przypisz formularz</DialogTitle>
          </DialogHeader>
          <div className="dialog-form">
            <div className="field">
              <Label>Formularz</Label>
              <Select value={assignData.form_id} onValueChange={(v) => setAssignData({...assignData, form_id: v})}>
                <SelectTrigger><SelectValue placeholder="Wybierz formularz" /></SelectTrigger>
                <SelectContent>
                  {forms.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="field">
              <Label>Klient</Label>
              <Select value={assignData.client_id} onValueChange={(v) => setAssignData({...assignData, client_id: v})}>
                <SelectTrigger><SelectValue placeholder="Wybierz klienta" /></SelectTrigger>
                <SelectContent>
                  {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="field">
              <Label>Częstotliwość</Label>
              <Select value={assignData.frequency} onValueChange={(v) => setAssignData({...assignData, frequency: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="once">Jednorazowo</SelectItem>
                  <SelectItem value="weekly">Co tydzień</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignFormOpen(false)}>Anuluj</Button>
            <Button onClick={assignForm} disabled={!assignData.form_id || !assignData.client_id}>Przypisz</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Responses Dialog */}
      <Dialog open={viewResponsesOpen} onOpenChange={setViewResponsesOpen}>
        <DialogContent className="responses-dialog">
          <DialogHeader>
            <DialogTitle>Odpowiedzi ({selectedFormResponses.length})</DialogTitle>
          </DialogHeader>
          <ScrollArea className="responses-scroll">
            {selectedFormResponses.length === 0 ? (
              <p className="responses-empty">Brak odpowiedzi</p>
            ) : (
              selectedFormResponses.map(resp => (
                <div key={resp.id} className="response-card">
                  <div className="response-header">
                    <strong>{resp.client_name}</strong>
                    <span className="response-date">{new Date(resp.submitted_at).toLocaleDateString('pl-PL')}</span>
                  </div>
                  <div className="response-fields">
                    {Object.entries(resp.responses || {}).map(([key, val]) => (
                      <div key={key} className="response-field">
                        <span className="response-label">{key}</span>
                        <span className="response-value">{typeof val === 'boolean' ? (val ? 'Tak' : 'Nie') : String(val) || '—'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Automation Dialog */}
      <Dialog open={automationOpen} onOpenChange={(open) => { setAutomationOpen(open); if (!open) setEditingAutomation(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingAutomation ? 'Edytuj automatyzację' : 'Nowa automatyzacja'}</DialogTitle>
          </DialogHeader>
          <div className="dialog-form">
            <div className="field">
              <Label>Nazwa</Label>
              <Input value={newAutomation.name} onChange={(e) => setNewAutomation({...newAutomation, name: e.target.value})} placeholder="Powitanie nowego klienta" />
            </div>
            <div className="field">
              <Label>Wyzwalacz (Gdy...)</Label>
              <Select value={newAutomation.trigger_type} onValueChange={(v) => setNewAutomation({...newAutomation, trigger_type: v, trigger_params: v === 'no_workout_x_days' ? { days: 3 } : {}})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="new_client">Nowy klient się połączy</SelectItem>
                  <SelectItem value="no_workout_x_days">Brak treningu przez X dni</SelectItem>
                  <SelectItem value="check_in_submitted">Check-in wysłany</SelectItem>
                  <SelectItem value="plan_completed">Plan ukończony</SelectItem>
                  <SelectItem value="measurement_logged">Pomiar zapisany</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {newAutomation.trigger_type === 'no_workout_x_days' && (
              <div className="field">
                <Label>Liczba dni bez treningu</Label>
                <Input type="number" min="1" value={newAutomation.trigger_params?.days || 3} onChange={(e) => setNewAutomation({...newAutomation, trigger_params: { days: parseInt(e.target.value) || 3 }})} />
              </div>
            )}
            <div className="field">
              <Label>Akcja (Wtedy...)</Label>
              <Select value={newAutomation.action_type} onValueChange={(v) => setNewAutomation({...newAutomation, action_type: v, action_params: v === 'send_message' ? { text: "" } : { form_id: "" }})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="send_message">Wyślij wiadomość</SelectItem>
                  <SelectItem value="assign_form">Przypisz formularz</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {newAutomation.action_type === 'send_message' && (
              <div className="field">
                <Label>Treść wiadomości</Label>
                <Textarea value={newAutomation.action_params?.text || ""} onChange={(e) => setNewAutomation({...newAutomation, action_params: { text: e.target.value }})} placeholder="Cześć! Witam w moim programie treningowym..." />
              </div>
            )}
            {newAutomation.action_type === 'assign_form' && (
              <div className="field">
                <Label>Formularz</Label>
                <Select value={newAutomation.action_params?.form_id || ""} onValueChange={(v) => setNewAutomation({...newAutomation, action_params: { form_id: v }})}>
                  <SelectTrigger><SelectValue placeholder="Wybierz formularz" /></SelectTrigger>
                  <SelectContent>
                    {forms.map(f => (
                      <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAutomationOpen(false)}>Anuluj</Button>
            <Button onClick={createAutomation} disabled={!newAutomation.name || (newAutomation.action_type === 'send_message' && !newAutomation.action_params?.text) || (newAutomation.action_type === 'assign_form' && !newAutomation.action_params?.form_id)}>
              {editingAutomation ? 'Zapisz' : 'Utwórz'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Builder Dialog */}
      <Dialog open={templateOpen} onOpenChange={(open) => { setTemplateOpen(open); if (!open) setEditingTemplate(null); }}>
        <DialogContent className="plan-dialog">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? 'Edytuj szablon' : 'Nowy szablon'}</DialogTitle>
          </DialogHeader>
          <div className="dialog-form">
            <div className="field">
              <Label>Nazwa szablonu</Label>
              <Input value={newTemplate.name} onChange={(e) => setNewTemplate({...newTemplate, name: e.target.value})} placeholder="Push Day" />
            </div>
            <div className="field-row">
              <div className="field" style={{flex:1}}>
                <Label>Kategoria</Label>
                <Select value={newTemplate.category} onValueChange={(v) => setNewTemplate({...newTemplate, category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">Ogólny</SelectItem>
                    <SelectItem value="strength">Siła</SelectItem>
                    <SelectItem value="cardio">Cardio</SelectItem>
                    <SelectItem value="flexibility">Rozciąganie</SelectItem>
                    <SelectItem value="sport">Sport</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="field">
              <Label>Opis <span className="optional">(opcjonalne)</span></Label>
              <Textarea value={newTemplate.description} onChange={(e) => setNewTemplate({...newTemplate, description: e.target.value})} placeholder="Opis programu..." />
            </div>
            <div className="field">
              <Label>Ćwiczenia</Label>
              {newTemplate.exercises.length > 0 && (
                <div className="selected-exercises">
                  {newTemplate.exercises.map((ex, i) => (
                    <div key={i} className="selected-exercise">
                      <span>{ex.name}</span>
                      <div className="exercise-config">
                        <Input type="number" value={ex.sets} onChange={(e) => {
                          const updated = [...newTemplate.exercises];
                          updated[i].sets = parseInt(e.target.value) || 0;
                          setNewTemplate({...newTemplate, exercises: updated});
                        }} className="small-input" />
                        <span>x</span>
                        <Input type="number" value={ex.reps} onChange={(e) => {
                          const updated = [...newTemplate.exercises];
                          updated[i].reps = parseInt(e.target.value) || 0;
                          setNewTemplate({...newTemplate, exercises: updated});
                        }} className="small-input" />
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => {
                        setNewTemplate({...newTemplate, exercises: newTemplate.exercises.filter((_, j) => j !== i)});
                      }}>
                        <X size={14} />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              <div className="exercise-picker">
                {exercises.filter(e => !newTemplate.exercises.find(pe => pe.id === e.id)).map(ex => (
                  <Button key={ex.id} variant="outline" size="sm" onClick={() => setNewTemplate({...newTemplate, exercises: [...newTemplate.exercises, { id: ex.id, name: ex.name, sets: 3, reps: 10 }]})}>
                    <Plus size={14} /> {ex.name}
                  </Button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTemplateOpen(false)}>Anuluj</Button>
            <Button onClick={createTemplate} disabled={!newTemplate.name || newTemplate.exercises.length === 0}>
              {editingTemplate ? 'Zapisz' : 'Utwórz'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Template Dialog */}
      <Dialog open={assignTemplateOpen} onOpenChange={setAssignTemplateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Przypisz szablon klientowi</DialogTitle>
            <DialogDescription>Utwórz plan treningowy z szablonu</DialogDescription>
          </DialogHeader>
          <div className="dialog-form">
            <div className="field">
              <Label>Szablon</Label>
              <Select value={assignTemplateData.template_id} onValueChange={(v) => setAssignTemplateData({...assignTemplateData, template_id: v})}>
                <SelectTrigger><SelectValue placeholder="Wybierz szablon" /></SelectTrigger>
                <SelectContent>
                  {programTemplates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="field">
              <Label>Klient</Label>
              <Select value={assignTemplateData.client_id} onValueChange={(v) => setAssignTemplateData({...assignTemplateData, client_id: v})}>
                <SelectTrigger><SelectValue placeholder="Wybierz klienta" /></SelectTrigger>
                <SelectContent>
                  {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="field">
              <Label>Data <span className="optional">(opcjonalne)</span></Label>
              <Input type="date" value={assignTemplateData.scheduled_date} onChange={(e) => setAssignTemplateData({...assignTemplateData, scheduled_date: e.target.value})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignTemplateOpen(false)}>Anuluj</Button>
            <Button onClick={assignTemplate} disabled={!assignTemplateData.template_id || !assignTemplateData.client_id}>Przypisz</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Session Dialog */}
      <Dialog open={sessionOpen} onOpenChange={setSessionOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nowa sesja</DialogTitle>
          </DialogHeader>
          <div className="dialog-form">
            <div className="field">
              <Label>Klient</Label>
              <Select value={newSession.client_id} onValueChange={(v) => setNewSession({...newSession, client_id: v})}>
                <SelectTrigger><SelectValue placeholder="Wybierz klienta" /></SelectTrigger>
                <SelectContent>
                  {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="field-row">
              <div className="field" style={{flex:1}}>
                <Label>Data</Label>
                <Input type="date" value={newSession.date} onChange={(e) => setNewSession({...newSession, date: e.target.value})} />
              </div>
              <div className="field" style={{flex:1}}>
                <Label>Godzina</Label>
                <Input type="time" value={newSession.time} onChange={(e) => setNewSession({...newSession, time: e.target.value})} />
              </div>
            </div>
            <div className="field-row">
              <div className="field" style={{flex:1}}>
                <Label>Czas (min)</Label>
                <Input type="number" value={newSession.duration} onChange={(e) => setNewSession({...newSession, duration: e.target.value})} />
              </div>
              <div className="field" style={{flex:1}}>
                <Label>Typ</Label>
                <Select value={newSession.type} onValueChange={(v) => setNewSession({...newSession, type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="training">Trening</SelectItem>
                    <SelectItem value="consultation">Konsultacja</SelectItem>
                    <SelectItem value="assessment">Ocena</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {firmLocations.length > 0 && (
              <div className="field">
                <Label>Lokalizacja <span className="optional">(opcjonalne)</span></Label>
                <select value={newSession.location_id || ""} onChange={(e) => setNewSession({...newSession, location_id: e.target.value})}
                        style={{width:'100%',padding:'8px',borderRadius:8,border:'1px solid var(--border)'}}>
                  <option value="">— brak —</option>
                  {firmLocations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>
            )}
            <div className="field">
              <Label>Notatki <span className="optional">(opcjonalne)</span></Label>
              <Textarea value={newSession.notes} onChange={(e) => setNewSession({...newSession, notes: e.target.value})} placeholder="Dodatkowe informacje..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSessionOpen(false)}>Anuluj</Button>
            <Button onClick={createSession} disabled={!newSession.client_id || !newSession.date}>Zaplanuj</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reassign / substitution Dialog */}
      <Dialog open={reassignOpen} onOpenChange={setReassignOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Zastępstwo — zmiana trenera</DialogTitle>
          </DialogHeader>
          <div className="dialog-form">
            {reassignSession && (
              <p style={{fontSize:13,color:'var(--text-secondary)'}}>
                {reassignSession.client_name} · {reassignSession.date} {reassignSession.time}
              </p>
            )}
            <div className="field">
              <Label>Nowy trener (z firmy)</Label>
              <select value={reassignTo} onChange={(e) => setReassignTo(e.target.value)}
                      style={{width:'100%',padding:'8px',borderRadius:8,border:'1px solid var(--border)'}}>
                <option value="">— wybierz —</option>
                {firmTrainers.filter(tr => !reassignSession || tr.id !== reassignSession.trainer_id).map(tr => (
                  <option key={tr.id} value={tr.id}>{tr.name}</option>
                ))}
              </select>
              {firmTrainers.length === 0 && (
                <p style={{fontSize:12,opacity:0.6,marginTop:6}}>Brak innych trenerów w firmie.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReassignOpen(false)}>Anuluj</Button>
            <Button onClick={doReassign} disabled={!reassignTo}>Przypisz zastępstwo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Availability Slot Dialog */}
      <Dialog open={availSlotOpen} onOpenChange={setAvailSlotOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Dodaj godziny dostępności</DialogTitle>
            <DialogDescription>Ustaw stałe godziny pracy na wybrany dzień tygodnia</DialogDescription>
          </DialogHeader>
          <div className="dialog-form">
            <div className="field">
              <Label>Dzień tygodnia</Label>
              <Select value={String(newAvailSlot.day_of_week)} onValueChange={(v) => setNewAvailSlot({...newAvailSlot, day_of_week: parseInt(v)})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {dayLabels.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="field">
              <Label>Sloty czasowe</Label>
              {newAvailSlot.slots.map((s, i) => (
                <div key={i} style={{display:'flex', gap:8, alignItems:'center', marginBottom:6}}>
                  <Input type="time" value={s.start_time} onChange={(e) => {
                    const slots = [...newAvailSlot.slots];
                    slots[i] = {...slots[i], start_time: e.target.value};
                    setNewAvailSlot({...newAvailSlot, slots});
                  }} style={{flex:1}} />
                  <span style={{color:'var(--text-secondary)'}}>–</span>
                  <Input type="time" value={s.end_time} onChange={(e) => {
                    const slots = [...newAvailSlot.slots];
                    slots[i] = {...slots[i], end_time: e.target.value};
                    setNewAvailSlot({...newAvailSlot, slots});
                  }} style={{flex:1}} />
                  {newAvailSlot.slots.length > 1 && (
                    <Button variant="ghost" size="sm" onClick={() => {
                      const slots = newAvailSlot.slots.filter((_, idx) => idx !== i);
                      setNewAvailSlot({...newAvailSlot, slots});
                    }}>
                      <X size={14} />
                    </Button>
                  )}
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => setNewAvailSlot({...newAvailSlot, slots: [...newAvailSlot.slots, { start_time: "10:00", end_time: "11:00" }]})}>
                <Plus size={14} /> Dodaj slot
              </Button>
            </div>
            {firmLocations.length > 0 ? (
              <div className="field">
                <Label>Lokalizacje <span className="optional">(do dyspozycji firmy)</span></Label>
                <div style={{display:'flex', flexWrap:'wrap', gap:8, marginTop:4}}>
                  {firmLocations.map((l) => {
                    const checked = (newAvailSlot.location_ids || []).includes(l.id);
                    return (
                      <button key={l.id} type="button"
                        onClick={() => {
                          const cur = newAvailSlot.location_ids || [];
                          const next = checked ? cur.filter((x) => x !== l.id) : [...cur, l.id];
                          setNewAvailSlot({...newAvailSlot, location_ids: next});
                        }}
                        style={{
                          cursor:'pointer', padding:'6px 12px', borderRadius:8, fontSize:13,
                          border: `1px solid ${checked ? 'var(--primary,#6366f1)' : 'var(--border,rgba(255,255,255,0.15)'}`,
                          background: checked ? 'var(--primary,#6366f1)' : 'transparent',
                          color: checked ? '#fff' : 'inherit',
                        }}>
                        📍 {l.name}
                      </button>
                    );
                  })}
                </div>
                <p style={{fontSize:11, color:'var(--text-secondary)', marginTop:6}}>
                  Brak zaznaczenia = dostępność we wszystkich Twoich lokalizacjach.
                </p>
              </div>
            ) : (
              <div className="field">
                <Label>Lokalizacja <span className="optional">(opcjonalne)</span></Label>
                <Input value={newAvailSlot.location} onChange={(e) => setNewAvailSlot({...newAvailSlot, location: e.target.value})} placeholder="np. Siłownia Centrum" />
              </div>
            )}
            <div className="field">
              <Label>Maks. klientów/slot</Label>
              <Input type="number" min="1" max="10" value={newAvailSlot.max_clients_per_slot} onChange={(e) => setNewAvailSlot({...newAvailSlot, max_clients_per_slot: e.target.value})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAvailSlotOpen(false)}>Anuluj</Button>
            <Button onClick={createAvailabilitySlot} disabled={newAvailSlot.slots.length === 0}>Zapisz</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Availability Exception Dialog */}
      <Dialog open={exceptionOpen} onOpenChange={setExceptionOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Dodaj wyjątek</DialogTitle>
            <DialogDescription>Zablokuj dzień lub dodaj dodatkowe godziny pracy</DialogDescription>
          </DialogHeader>
          <div className="dialog-form">
            <div className="field">
              <Label>Data</Label>
              <Input type="date" value={newException.date} onChange={(e) => setNewException({...newException, date: e.target.value})} />
            </div>
            <div className="field">
              <Label>Typ</Label>
              <Select value={newException.available ? "extra" : "blocked"} onValueChange={(v) => setNewException({...newException, available: v === "extra"})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="blocked">Dzień wolny (brak dostępności)</SelectItem>
                  <SelectItem value="extra">Dodatkowe godziny</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="field">
              <Label>Powód <span className="optional">(opcjonalne)</span></Label>
              <Input value={newException.reason} onChange={(e) => setNewException({...newException, reason: e.target.value})} placeholder="np. Urlop, szkolenie..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExceptionOpen(false)}>Anuluj</Button>
            <Button onClick={createException} disabled={!newException.date}>Dodaj</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Leave / L4 Dialog */}
      <Dialog open={leaveOpen} onOpenChange={setLeaveOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("trainer.leave.add")}</DialogTitle>
            <DialogDescription>{t("trainer.leave.sessionsWillCancel")}</DialogDescription>
          </DialogHeader>
          <div className="dialog-form">
            <div className="field">
              <Label>{t("trainer.leave.type")}</Label>
              <Select value={newLeave.type} onValueChange={(v) => setNewLeave({...newLeave, type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="vacation">{t("trainer.leave.vacation")}</SelectItem>
                  <SelectItem value="sick_leave">{t("trainer.leave.sickLeave")}</SelectItem>
                  <SelectItem value="training">{t("trainer.leave.training")}</SelectItem>
                  <SelectItem value="personal">{t("trainer.leave.personal")}</SelectItem>
                  <SelectItem value="other">{t("trainer.leave.other")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="field-row">
              <div className="field" style={{flex:1}}>
                <Label>{t("trainer.leave.from")}</Label>
                <Input type="date" value={newLeave.date_from} onChange={(e) => setNewLeave({...newLeave, date_from: e.target.value})} />
              </div>
              <div className="field" style={{flex:1}}>
                <Label>{t("trainer.leave.to")}</Label>
                <Input type="date" value={newLeave.date_to} min={newLeave.date_from} onChange={(e) => setNewLeave({...newLeave, date_to: e.target.value})} />
              </div>
            </div>
            <div className="field">
              <Label>{t("trainer.leave.reason")}</Label>
              <Input value={newLeave.reason} onChange={(e) => setNewLeave({...newLeave, reason: e.target.value})} placeholder="np. Wyjazd rodzinny, rehabilitacja..." />
            </div>
            {newLeave.type === 'sick_leave' && (
              <div className="field">
                <Label>{t("trainer.leave.documentUrl")}</Label>
                <Input value={newLeave.document_url} onChange={(e) => setNewLeave({...newLeave, document_url: e.target.value})} placeholder="https://... (link do skanu L4)" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLeaveOpen(false)}>{t("action.cancel")}</Button>
            <Button onClick={createLeave} disabled={!newLeave.date_from || !newLeave.date_to}>{t("action.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Meal Plan Dialog */}
      <Dialog open={mealPlanOpen} onOpenChange={setMealPlanOpen}>
        <DialogContent className="plan-dialog">
          <DialogHeader>
            <DialogTitle>Nowy plan żywieniowy</DialogTitle>
          </DialogHeader>
          <div className="dialog-form">
            <div className="field">
              <Label>Nazwa</Label>
              <Input value={newMealPlan.name} onChange={(e) => setNewMealPlan({...newMealPlan, name: e.target.value})} placeholder="Plan 2000 kcal" />
            </div>
            <div className="field">
              <Label>Klient</Label>
              <Select value={newMealPlan.client_id} onValueChange={(v) => setNewMealPlan({...newMealPlan, client_id: v})}>
                <SelectTrigger><SelectValue placeholder="Wybierz klienta" /></SelectTrigger>
                <SelectContent>
                  {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="field-row">
              <div className="field">
                <Label>Kalorie</Label>
                <Input type="number" value={newMealPlan.target_calories} onChange={(e) => setNewMealPlan({...newMealPlan, target_calories: e.target.value})} placeholder="2000" />
              </div>
              <div className="field">
                <Label>Białko (g)</Label>
                <Input type="number" value={newMealPlan.target_protein} onChange={(e) => setNewMealPlan({...newMealPlan, target_protein: e.target.value})} placeholder="150" />
              </div>
              <div className="field">
                <Label>Węgle (g)</Label>
                <Input type="number" value={newMealPlan.target_carbs} onChange={(e) => setNewMealPlan({...newMealPlan, target_carbs: e.target.value})} placeholder="200" />
              </div>
              <div className="field">
                <Label>Tłuszcze (g)</Label>
                <Input type="number" value={newMealPlan.target_fat} onChange={(e) => setNewMealPlan({...newMealPlan, target_fat: e.target.value})} placeholder="65" />
              </div>
            </div>
            <div className="field">
              <Label>Posiłki</Label>
              {newMealPlan.meals.map((meal, mi) => (
                <div key={mi} className="meal-plan-meal">
                  <div className="meal-plan-meal-header">
                    <Badge>{mealTypeLabels[meal.meal_type] || meal.meal_type}</Badge>
                    <Button variant="ghost" size="sm" onClick={() => setNewMealPlan({...newMealPlan, meals: newMealPlan.meals.filter((_, j) => j !== mi)})}>
                      <X size={14} />
                    </Button>
                  </div>
                  {meal.items.map((item, ii) => (
                    <div key={ii} className="meal-item-row">
                      <Input placeholder="Nazwa" value={item.name} onChange={(e) => {
                        const meals = [...newMealPlan.meals];
                        meals[mi].items[ii].name = e.target.value;
                        setNewMealPlan({...newMealPlan, meals});
                      }} />
                      <Input type="number" placeholder="kcal" className="small-input" value={item.calories || ""} onChange={(e) => {
                        const meals = [...newMealPlan.meals];
                        meals[mi].items[ii].calories = parseFloat(e.target.value) || 0;
                        setNewMealPlan({...newMealPlan, meals});
                      }} />
                      <Input type="number" placeholder="B" className="small-input" value={item.protein || ""} onChange={(e) => {
                        const meals = [...newMealPlan.meals];
                        meals[mi].items[ii].protein = parseFloat(e.target.value) || 0;
                        setNewMealPlan({...newMealPlan, meals});
                      }} />
                      <Button variant="ghost" size="sm" onClick={() => {
                        const meals = [...newMealPlan.meals];
                        meals[mi].items = meals[mi].items.filter((_, j) => j !== ii);
                        setNewMealPlan({...newMealPlan, meals});
                      }}><X size={12} /></Button>
                    </div>
                  ))}
                  <Button variant="ghost" size="sm" onClick={() => {
                    const meals = [...newMealPlan.meals];
                    meals[mi].items.push({ name: "", calories: 0, protein: 0, carbs: 0, fat: 0, portion: "" });
                    setNewMealPlan({...newMealPlan, meals});
                  }}><Plus size={12} /> Dodaj produkt</Button>
                </div>
              ))}
              <div className="meal-type-buttons">
                {["breakfast", "lunch", "dinner", "snack"].filter(mt => !newMealPlan.meals.find(m => m.meal_type === mt)).map(mt => (
                  <Button key={mt} variant="outline" size="sm" onClick={() => addMealToPlan(mt)}>
                    <Plus size={14} /> {mealTypeLabels[mt]}
                  </Button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMealPlanOpen(false)}>Anuluj</Button>
            <Button onClick={createMealPlan} disabled={!newMealPlan.name || !newMealPlan.client_id}>Utwórz</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Exercise Dialog */}
      <Dialog open={exerciseOpen} onOpenChange={(open) => { setExerciseOpen(open); if (!open) setEditingExercise(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingExercise ? 'Edytuj ćwiczenie' : 'Nowe ćwiczenie'}</DialogTitle>
          </DialogHeader>
          <div className="dialog-form">
            <div className="field">
              <Label>Nazwa</Label>
              <Input value={newExercise.name} onChange={(e) => setNewExercise({...newExercise, name: e.target.value})} placeholder="Przysiad ze sztangą" />
            </div>
            <div className="field">
              <Label>Kategoria</Label>
              <Select value={newExercise.category} onValueChange={(v) => setNewExercise({...newExercise, category: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="strength">Siła</SelectItem>
                  <SelectItem value="cardio">Cardio</SelectItem>
                  <SelectItem value="flexibility">Rozciąganie</SelectItem>
                  <SelectItem value="core">Core</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="field">
              <Label>URL Video <span className="optional">(opcjonalne)</span></Label>
              <Input value={newExercise.video_url || ""} onChange={(e) => setNewExercise({...newExercise, video_url: e.target.value})} placeholder="https://..." />
            </div>
            <div className="field">
              <Label>Opis <span className="optional">(opcjonalne)</span></Label>
              <Textarea value={newExercise.description || ""} onChange={(e) => setNewExercise({...newExercise, description: e.target.value})} />
            </div>
            <div className="field-group">
              <Label>Timer Tabata <span className="optional">(opcjonalne)</span></Label>
              <div className="tabata-inputs">
                <div className="field">
                  <Label>Praca (s)</Label>
                  <Input type="number" value={newExercise.tabata_work || ""} onChange={(e) => setNewExercise({...newExercise, tabata_work: e.target.value})} placeholder="20" />
                </div>
                <div className="field">
                  <Label>Przerwa (s)</Label>
                  <Input type="number" value={newExercise.tabata_rest || ""} onChange={(e) => setNewExercise({...newExercise, tabata_rest: e.target.value})} placeholder="10" />
                </div>
                <div className="field">
                  <Label>Rundy</Label>
                  <Input type="number" value={newExercise.tabata_rounds || ""} onChange={(e) => setNewExercise({...newExercise, tabata_rounds: e.target.value})} placeholder="8" />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setExerciseOpen(false); setEditingExercise(null); }}>Anuluj</Button>
            <Button onClick={createExercise}>{editingExercise ? 'Zapisz' : 'Utwórz'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Plan Dialog */}
      <Dialog open={planOpen} onOpenChange={setPlanOpen}>
        <DialogContent className="plan-dialog">
          <DialogHeader>
            <DialogTitle>Nowy plan treningowy</DialogTitle>
          </DialogHeader>
          <div className="dialog-form">
            <div className="field">
              <Label>Nazwa planu</Label>
              <Input value={newPlan.name} onChange={(e) => setNewPlan({...newPlan, name: e.target.value})} placeholder="Dzień nóg" />
            </div>
            <div className="field">
              <Label>Klient</Label>
              <Select value={newPlan.client_id} onValueChange={(v) => setNewPlan({...newPlan, client_id: v})}>
                <SelectTrigger><SelectValue placeholder="Wybierz klienta" /></SelectTrigger>
                <SelectContent>
                  {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            
            <div className="field">
              <Label>Exercises</Label>
              {newPlan.exercises.length > 0 && (
                <div className="selected-exercises">
                  {newPlan.exercises.map((ex, i) => (
                    <div key={i} className="selected-exercise">
                      <span>{ex.name}</span>
                      <div className="exercise-config">
                        <Input
                          type="number"
                          value={ex.sets}
                          onChange={(e) => {
                            const updated = [...newPlan.exercises];
                            updated[i].sets = parseInt(e.target.value) || 0;
                            setNewPlan({...newPlan, exercises: updated});
                          }}
                          className="small-input"
                        />
                        <span>x</span>
                        <Input
                          type="number"
                          value={ex.reps}
                          onChange={(e) => {
                            const updated = [...newPlan.exercises];
                            updated[i].reps = parseInt(e.target.value) || 0;
                            setNewPlan({...newPlan, exercises: updated});
                          }}
                          className="small-input"
                        />
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => removeExerciseFromPlan(i)}>
                        <X size={14} />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              <div className="exercise-picker">
                {exercises.filter(e => !newPlan.exercises.find(pe => pe.id === e.id)).map(ex => (
                  <Button key={ex.id} variant="outline" size="sm" onClick={() => addExerciseToPlan(ex)}>
                    <Plus size={14} /> {ex.name}
                  </Button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlanOpen(false)}>Anuluj</Button>
            <Button onClick={createPlan} disabled={!newPlan.name || !newPlan.client_id || newPlan.exercises.length === 0}>
              Utwórz plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Habit Dialog */}
      <Dialog open={habitOpen} onOpenChange={(open) => { setHabitOpen(open); if (!open) setEditingHabit(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingHabit ? 'Edytuj nawyk' : 'Nowy nawyk'}</DialogTitle>
          </DialogHeader>
          <div className="dialog-form">
            <div className="field">
              <Label>Nazwa nawyku</Label>
              <Input 
                value={newHabit.name} 
                onChange={(e) => setNewHabit({...newHabit, name: e.target.value})} 
                placeholder="np. Pij 2L wody" 
              />
            </div>
            <div className="field">
              <Label>Opis <span className="optional">(opcjonalne)</span></Label>
              <Textarea 
                value={newHabit.description} 
                onChange={(e) => setNewHabit({...newHabit, description: e.target.value})} 
                placeholder="Dlaczego ten nawyk jest ważny..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setHabitOpen(false); setEditingHabit(null); }}>Anuluj</Button>
            <Button onClick={createOrUpdateHabit} disabled={!newHabit.name}>{editingHabit ? 'Zapisz' : 'Utwórz'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Client Edit Dialog */}
      <Dialog open={clientEditOpen} onOpenChange={setClientEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edytuj klienta: {editingClient?.name}</DialogTitle>
          </DialogHeader>
          <div className="dialog-form">
            <div className="field">
              <Label>Notatki trenera</Label>
              <Textarea 
                value={clientNotes.notes} 
                onChange={(e) => setClientNotes({...clientNotes, notes: e.target.value})} 
                placeholder="Notatki o kliencie..."
                rows={3}
              />
            </div>
            <div className="field">
              <Label>Cele</Label>
              <Textarea
                value={clientNotes.goals}
                onChange={(e) => setClientNotes({...clientNotes, goals: e.target.value})}
                placeholder="Cele klienta..."
                rows={3}
              />
            </div>
            {firmLocations.length > 0 && (
              <div className="field">
                <Label>Lokalizacje <span className="optional">(gdzie trenuje ten klient)</span></Label>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 4 }}>
                  {firmLocations.map((l) => {
                    const on = clientNotes.location_ids.includes(l.id);
                    return (
                      <label key={l.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
                        <Checkbox checked={on} onCheckedChange={(v) => setClientNotes({
                          ...clientNotes,
                          location_ids: v ? [...clientNotes.location_ids, l.id] : clientNotes.location_ids.filter(x => x !== l.id),
                        })} />
                        {l.name}
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => downloadClientProgressPdf(editingClient)} style={{ marginRight: "auto" }}>
              <FileDown size={16} /> Raport PDF
            </Button>
            <Button variant="outline" onClick={() => setClientEditOpen(false)}>Anuluj</Button>
            <Button onClick={saveClientNotes}>Zapisz</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Video Review Dialog */}
      <Dialog open={videoReviewOpen} onOpenChange={setVideoReviewOpen}>
        <DialogContent className="video-dialog">
          <DialogHeader>
            <DialogTitle>Review: {selectedVideo?.exercise_name}</DialogTitle>
          </DialogHeader>
          {selectedVideo && (
            <div className="video-review-content">
              <div className="video-player">
                <video controls src={selectedVideo.video_url} />
              </div>
              {selectedVideo.notes && (
                <div className="client-notes">
                  <strong>Client notes:</strong> {selectedVideo.notes}
                </div>
              )}
              {selectedVideo.trainer_comments?.length > 0 && (
                <div className="existing-comments">
                  <strong>Your comments:</strong>
                  {selectedVideo.trainer_comments.map((c, i) => (
                    <div key={i} className="comment">{c.comment}</div>
                  ))}
                </div>
              )}
              <div className="field">
                <Label>Add Feedback</Label>
                <Textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Great form! Try to keep your back straight..."
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setVideoReviewOpen(false)}>Close</Button>
            <Button onClick={addVideoComment} disabled={!newComment.trim()}>
              <Send size={14} /> Send Feedback
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notifications Dialog */}
      <Dialog open={notifOpen} onOpenChange={setNotifOpen}>
        <DialogContent className="notif-dialog">
          <DialogHeader>
            <DialogTitle>Powiadomienia</DialogTitle>
          </DialogHeader>
          <ScrollArea className="notif-scroll">
            {notifications.length === 0 ? (
              <p className="notif-empty">Brak powiadomień</p>
            ) : (
              <div className="notif-list">
                {notifications.map(n => (
                  <div key={n.id} className={`notif-item ${n.read ? '' : 'unread'}`}>
                    <div className="notif-icon">
                      {n.type === 'automation' && <Zap size={16} />}
                      {n.type === 'workout' && <Dumbbell size={16} />}
                      {n.type === 'form' && <ClipboardList size={16} />}
                      {n.type === 'session' && <Calendar size={16} />}
                      {n.type === 'message' && <MessageCircle size={16} />}
                      {n.type === 'ticket' && <LifeBuoy size={16} />}
                      {!['automation', 'workout', 'form', 'session', 'message', 'ticket'].includes(n.type) && <Bell size={16} />}
                    </div>
                    <div className="notif-content">
                      <p className="notif-title">{n.title}</p>
                      <p className="notif-message">{n.message}</p>
                      <span className="notif-time">{format(new Date(n.created_at), "d MMM, HH:mm")}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* AI WORKOUT PLAN DIALOG */}
      <Dialog open={aiWorkoutOpen} onOpenChange={setAiWorkoutOpen}>
        <DialogContent className="dialog-content" style={{maxWidth:560}}>
          <DialogHeader>
            <DialogTitle><Zap size={20} /> AI Plan treningowy</DialogTitle>
            <DialogDescription>AI wygeneruje spersonalizowany plan treningowy na podstawie historii klienta i dostepnych cwiczen.</DialogDescription>
          </DialogHeader>

          {!aiWorkoutPlan ? (
            <div style={{display:'flex', flexDirection:'column', gap:12}}>
              <div>
                <Label>Klient</Label>
                <Select value={aiWorkoutForm.client_id} onValueChange={v => setAiWorkoutForm(p => ({...p, client_id: v}))}>
                  <SelectTrigger><SelectValue placeholder="Wybierz klienta" /></SelectTrigger>
                  <SelectContent>
                    {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Cel</Label>
                <Select value={aiWorkoutForm.goal} onValueChange={v => setAiWorkoutForm(p => ({...p, goal: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="strength">Sila</SelectItem>
                    <SelectItem value="hypertrophy">Hipertrofia (masa)</SelectItem>
                    <SelectItem value="endurance">Wytrzymalosc silowa</SelectItem>
                    <SelectItem value="weight_loss">Redukcja</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:8}}>
                <div>
                  <Label>Dni/tydzien</Label>
                  <Input type="number" value={aiWorkoutForm.days_per_week} onChange={e => setAiWorkoutForm(p => ({...p, days_per_week: parseInt(e.target.value) || 3}))} min={1} max={7} />
                </div>
                <div>
                  <Label>Czas sesji (min)</Label>
                  <Input type="number" value={aiWorkoutForm.session_minutes} onChange={e => setAiWorkoutForm(p => ({...p, session_minutes: parseInt(e.target.value) || 60}))} min={15} max={120} />
                </div>
              </div>
              <div>
                <Label>Sprzet</Label>
                <Select value={aiWorkoutForm.equipment} onValueChange={v => setAiWorkoutForm(p => ({...p, equipment: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full_gym">Pelna silownia</SelectItem>
                    <SelectItem value="dumbbells_only">Tylko hantle</SelectItem>
                    <SelectItem value="bodyweight">Cwiczenia z cialem</SelectItem>
                    <SelectItem value="home">Trening domowy</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Dodatkowe uwagi (opcjonalnie)</Label>
                <Textarea value={aiWorkoutForm.notes} onChange={e => setAiWorkoutForm(p => ({...p, notes: e.target.value}))} placeholder="Np. kontuzja kolana, unikac..." rows={2} />
              </div>
              <Button onClick={handleGenerateAiWorkout} disabled={aiWorkoutLoading || !aiWorkoutForm.client_id}>
                {aiWorkoutLoading ? <><Clock size={16} className="animate-spin" /> Generowanie...</> : <><Zap size={16} /> Generuj plan</>}
              </Button>
            </div>
          ) : (
            <div style={{display:'flex', flexDirection:'column', gap:12}}>
              <Card>
                <CardHeader style={{paddingBottom:8}}>
                  <CardTitle style={{fontSize:16}}>{aiWorkoutPlan.name}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p style={{fontSize:13, fontWeight:600, marginBottom:8}}>Cwiczenia ({aiWorkoutPlan.exercises?.length})</p>
                  <div style={{display:'flex', flexDirection:'column', gap:6, maxHeight:250, overflow:'auto'}}>
                    {aiWorkoutPlan.exercises?.map((ex, i) => {
                      const exInfo = exercises.find(e => e.id === ex.exercise_id);
                      return (
                        <div key={i} style={{padding:'8px 10px', borderRadius:6, background:'var(--bg-secondary)', fontSize:13}}>
                          <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                            <span style={{fontWeight:500}}>{ex.order}. {exInfo?.name || ex.exercise_id}</span>
                            <Badge variant="outline">{ex.sets?.length} serii</Badge>
                          </div>
                          <div style={{display:'flex', gap:8, marginTop:4, color:'var(--text-secondary)', fontSize:12}}>
                            {ex.sets?.map((s, si) => (
                              <span key={si}>{s.target_reps}x{s.target_weight ? `${s.target_weight}kg` : 'BW'}</span>
                            ))}
                          </div>
                          {ex.notes && <p style={{marginTop:4, fontSize:12, color:'var(--text-secondary)'}}>{ex.notes}</p>}
                        </div>
                      );
                    })}
                  </div>
                  {aiWorkoutPlan.notes && <p style={{marginTop:8, fontSize:13, color:'var(--text-secondary)'}}>{aiWorkoutPlan.notes}</p>}
                </CardContent>
              </Card>

              {aiWorkoutNotes && (
                <Card style={{borderColor:'var(--primary)'}}>
                  <CardContent style={{padding:12}}>
                    <p style={{fontSize:13, fontWeight:600, marginBottom:4}}><Star size={14} /> Sugestie AI dla trenera</p>
                    <p style={{fontSize:13, color:'var(--text-secondary)'}}>{aiWorkoutNotes}</p>
                  </CardContent>
                </Card>
              )}

              <div style={{display:'flex', gap:8}}>
                <Button style={{flex:1}} onClick={handleAcceptAiWorkout}><Check size={16} /> Zapisz plan</Button>
                <Button variant="outline" style={{flex:1}} onClick={() => setAiWorkoutPlan(null)}><Edit size={16} /> Generuj ponownie</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={broadcastOpen} onOpenChange={setBroadcastOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ogłoszenie do zespołu</DialogTitle>
            <DialogDescription>
              {orgRole === "manager"
                ? "Trafi do trenerów Twojej lokalizacji."
                : "Trafi do wszystkich trenerów w firmie."}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={broadcastText}
            onChange={(e) => setBroadcastText(e.target.value)}
            placeholder="Treść ogłoszenia..."
            rows={4}
            data-testid="broadcast-text"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setBroadcastOpen(false)}>Anuluj</Button>
            <Button onClick={sendBroadcast} disabled={broadcastSending || !broadcastText.trim()} data-testid="broadcast-send">
              {broadcastSending ? "Wysyłam..." : "Wyślij"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <HelpButton />

      {/* Exercise GIF preview */}
      {gifPreview && (
        <div onClick={() => setGifPreview(null)}
          style={{ position: "fixed", inset: 0, zIndex: 140, background: "rgba(0,0,0,0.8)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <button type="button" onClick={() => setGifPreview(null)} aria-label="Zamknij"
            style={{ position: "absolute", top: 16, right: 16, width: 40, height: 40, borderRadius: "50%", border: "none", cursor: "pointer", background: "rgba(255,255,255,0.15)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <X size={22} />
          </button>
          <img src={gifPreview.url} alt={gifPreview.name} onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "92vw", maxHeight: "78vh", borderRadius: 14, background: "#fff", objectFit: "contain", boxShadow: "0 20px 60px rgba(0,0,0,0.5)" }} />
          <div style={{ color: "#fff", fontSize: 16, fontWeight: 600, marginTop: 14, textAlign: "center" }}>{gifPreview.name}</div>
        </div>
      )}
    </div>
  );
};


export default TrainerDashboard;
